import { db } from "./dbClient";

import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import { useTheme } from "./theme";
import { useAuditLog } from "./useAuditLog";
import ConfirmDialog from "./ConfirmDialog";
import { useToast, ToastContainer } from "./Toast";
import { printToPDF } from "./exportUtils";

// ─── NOTIFICATIONS ────────────────────────────────────────────────────────────
const NOTIF_KEY = "crg_policy_notifications";

export function getPolicyNotifications() {
  try { return JSON.parse(localStorage.getItem(NOTIF_KEY) || "[]"); } catch { return []; }
}

function saveNotifications(list) {
  try { localStorage.setItem(NOTIF_KEY, JSON.stringify(list)); } catch {}
}

export function addPolicyNotification(notif) {
  const list = getPolicyNotifications();
  list.unshift({ ...notif, id: Date.now(), read: false, ts: new Date().toISOString() });
  saveNotifications(list.slice(0, 50)); // keep last 50
}

export function markAllPolicyNotificationsRead() {
  const list = getPolicyNotifications().map(n => ({ ...n, read: true }));
  saveNotifications(list);
}

// ─── ASSIGN FOR REVIEW MODAL ──────────────────────────────────────────────────
function AssignReviewModal({ doc, team, currentUser, company, onClose, onAssigned }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  const assignable = (team || []).filter(m => m.id !== currentUser.id && m.status !== "invited");

  async function assign() {
    if (!selected) return;
    setSending(true);

    // Legacy localStorage notification (keep for backward compat)
    addPolicyNotification({
      type: "review_request",
      docId: doc.id,
      docTitle: doc.title,
      fromId: currentUser.id,
      fromName: `${currentUser.firstName} ${currentUser.lastName}`,
      toId: selected.id,
      toName: `${selected.firstName} ${selected.lastName}`,
      note: note.trim(),
    });

    // Write to unified assignments table
    if (company?.id) {
      try {
        await db.from("assignments").insert({
          company_id:  company.id,
          assigned_by: currentUser.id,
          assigned_to: selected.id,
          type:        "policy",
          ref_id:      String(doc.id),
          ref_title:   doc.title,
          status:      "pending",
        });
      } catch (e) { console.warn("[assign] policy assignment failed:", e.message); }

      try {
        await db.from("notifications").insert({
          company_id:  company.id,
          user_id:     selected.id,
          type:        "todo_assigned",
          title:       `Policy review assigned: ${doc.title}`,
          body:        `${currentUser.firstName} ${currentUser.lastName} has assigned "${doc.title}" to you for review.${note.trim() ? " Note: " + note.trim() : ""}`,
          link_module: "policies",
          link_id:     String(doc.id),
        });
      } catch (e) { console.warn("[notify] policy notification failed:", e.message); }
    }

    onAssigned({ ...doc, status: "review", reviewAssignedTo: selected.id, reviewAssigneeName: `${selected.firstName} ${selected.lastName}`, reviewNote: note.trim(), reviewRequestedAt: new Date().toISOString() });
    setSending(false);
    onClose();
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, width: 480, fontFamily: "'DM Mono', monospace", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: 32, paddingBottom: 16, borderBottom: `1px solid ${C.border}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
            <div style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: 18, fontWeight: 700 }}>{t('policies.assignForReview')}</div>
            <button onClick={onClose} style={{ background: "transparent", border: "none", color: C.textDim, cursor: "pointer", fontSize: 18 }}>✕</button>
          </div>
          <p style={{ color: C.textMid, fontSize: 13, margin: "0", lineHeight: 1.6 }}>
            {t('policies.assignDesc1')} <span style={{ color: C.accent }}>"{doc.title}"</span> {t('policies.assignDesc2')} <span style={{ color: C.warn }}>{t('policies.reviewStatus')}</span>.
          </p>
        </div>
        <div style={{ overflow: "auto", flex: 1, padding: "20px 32px" }}>

        {assignable.length === 0 ? (
          <div style={{ color: C.textDim, fontSize: 13, textAlign: "center", padding: "20px 0" }}>
            {t('policies.noTeamMembers')}
          </div>
        ) : (
          <>
            <div style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.08em", marginBottom: 10 }}>{t('policies.selectReviewer')}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
              {assignable.map(m => (
                <div key={m.id} onClick={() => setSelected(m)} style={{
                  display: "flex", alignItems: "center", gap: 12, padding: "12px 14px",
                  borderRadius: 9, cursor: "pointer", transition: "all 0.12s",
                  background: selected?.id === m.id ? C.accentGlow : C.bg,
                  border: `1px solid ${selected?.id === m.id ? C.accent : C.border}`,
                }}>
                  <div style={{ width: 36, height: 36, borderRadius: "50%", background: `linear-gradient(135deg, ${C.borderLight}, ${C.border})`, display: "flex", alignItems: "center", justifyContent: "center", color: C.textMid, fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                    {m.firstName?.[0]}{m.lastName?.[0]}
                  </div>
                  <div>
                    <div style={{ color: C.text, fontWeight: 600, fontSize: 13 }}>{m.firstName} {m.lastName}</div>
                    <div style={{ color: C.textDim, fontSize: 11 }}>{m.role} · {m.email}</div>
                  </div>
                  {selected?.id === m.id && <span style={{ marginLeft: "auto", color: C.accent, fontSize: 16 }}>✓</span>}
                </div>
              ))}
            </div>

            <div style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.08em", marginBottom: 8 }}>{t('policies.noteOptional')}</div>
            <textarea
              value={note} onChange={e => setNote(e.target.value)}
              placeholder={t('policies.noteReviewerPlaceholder')}
              rows={3}
              style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 13, padding: "9px 12px", fontFamily: "'DM Mono', monospace", resize: "none", outline: "none", boxSizing: "border-box" }}
            />
          </>
        )}
        </div>
        <div style={{ padding: "16px 32px 32px", borderTop: `1px solid ${C.border}`, display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <Btn variant="ghost" onClick={onClose}>{t('common.cancel')}</Btn>
          <Btn onClick={assign} disabled={!selected || sending}>
            {sending ? t('common.sending') : `✓ ${t('policies.assignForReview')}`}
          </Btn>
        </div>
      </div>
    </div>
  );
}

// ─── NOTIFICATIONS PANEL ──────────────────────────────────────────────────────
function NotificationsPanel({ currentUser, onClose }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [notifs, setNotifs] = useState(() => getPolicyNotifications());

  // My notifications = ones assigned TO me
  const mine = notifs.filter(n => n.toId === currentUser.id || n.fromId === currentUser.id);
  const unread = mine.filter(n => !n.read).length;

  function markRead() {
    markAllPolicyNotificationsRead();
    setNotifs(getPolicyNotifications());
  }

  function timeAgo(ts) {
    const diff = Date.now() - new Date(ts).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  }

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, display: "flex", alignItems: "flex-start", justifyContent: "flex-end" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ marginTop: 60, marginRight: 24, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, width: 380, maxHeight: "70vh", display: "flex", flexDirection: "column", fontFamily: "'DM Mono', monospace", boxShadow: "0 8px 32px rgba(0,0,0,0.5)" }}>
        <div style={{ padding: "16px 18px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ color: C.text, fontWeight: 700, fontSize: 15, fontFamily: "'Syne', sans-serif" }}>{t('notifications.title')}</span>
          {unread > 0 && <span style={{ background: C.danger, color: "#fff", fontSize: 10, padding: "2px 7px", borderRadius: 10, fontWeight: 700 }}>{unread}</span>}
          {unread > 0 && <button onClick={markRead} style={{ marginLeft: "auto", background: "transparent", border: "none", color: C.accent, fontSize: 11, cursor: "pointer" }}>{t('notifications.markAllRead')}</button>}
        </div>
        <div style={{ overflowY: "auto", flex: 1 }}>
          {mine.length === 0 ? (
            <div style={{ color: C.textDim, fontSize: 13, textAlign: "center", padding: "30px 0" }}>{t('notifications.noNotifications')}</div>
          ) : mine.map(n => (
            <div key={n.id} style={{ padding: "14px 18px", borderBottom: `1px solid ${C.border}`, background: n.read ? "transparent" : `${C.accentGlow}`, transition: "background 0.2s" }}>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                <span style={{ fontSize: 18, flexShrink: 0 }}>{n.toId === currentUser.id ? "📋" : "📤"}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: C.text, fontSize: 12, lineHeight: 1.6 }}>
                    {n.toId === currentUser.id
                      ? <><span style={{ color: C.accent }}>{n.fromName}</span> {t('notifications.assignedToYou1')} <span style={{ color: C.warn }}>"{n.docTitle}"</span> {t('notifications.assignedToYou2')}</>
                      : <>{t('notifications.youAssigned1')} <span style={{ color: C.warn }}>"{n.docTitle}"</span> {t('notifications.youAssigned2')} <span style={{ color: C.accent }}>{n.toName}</span> {t('notifications.youAssigned3')}</>
                    }
                  </div>
                  {n.note && <div style={{ color: C.textDim, fontSize: 11, marginTop: 4, fontStyle: "italic" }}>"{n.note}"</div>}
                  <div style={{ color: C.textDim, fontSize: 10, marginTop: 4 }}>{timeAgo(n.ts)}</div>
                </div>
                {!n.read && <div style={{ width: 7, height: 7, borderRadius: "50%", background: C.accent, flexShrink: 0, marginTop: 4 }} />}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const CATEGORIES = ["Core", "KYC", "Sanctions", "TM", "Reporting", "Governance", "Training", "Other"];
// E-027 / F-020: Added pending_approval status for approval workflow
const STATUSES = ["draft", "review", "pending_approval", "approved"];

// E-028: 1-year review period
const POLICY_REVIEW_DAYS = 365;

function Badge({ status }) {
  const C = useTheme();
  // Ensure status is a string
  const statusStr = typeof status === 'string' ? status : String(status || '');
  const s = {
    approved:         { bg: "rgba(0,230,118,0.1)",  color: C.success,           border: "rgba(0,230,118,0.3)" },
    review:           { bg: "rgba(255,140,0,0.1)",   color: C.warn,              border: "rgba(255,140,0,0.3)" },
    pending_approval: { bg: "rgba(167,139,250,0.1)", color: "#A78BFA",           border: "rgba(167,139,250,0.3)" },
    draft:            { bg: "rgba(138,155,181,0.1)", color: C.textMid,           border: "rgba(138,155,181,0.3)" },
  }[statusStr] || { bg: "transparent", color: C.textDim, border: "transparent" };
  return (
    <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4, background: s.bg, color: s.color, border: `1px solid ${s.border}`, fontFamily: "'DM Mono', monospace", textTransform: "uppercase", letterSpacing: "0.06em" }}>
      {statusStr.replace("_", " ")}
    </span>
  );
}

function isReviewOverdue(doc) {
  if (!doc.updated) return false;
  const updatedMs = new Date(doc.next_review_date || doc.updated).getTime();
  const overdueDateMs = doc.next_review_date
    ? new Date(doc.next_review_date).getTime()
    : updatedMs + POLICY_REVIEW_DAYS * 24 * 60 * 60 * 1000;
  return Date.now() > overdueDateMs;
}

function Btn({ children, onClick, variant = "primary", disabled = false, small = false, style: extraStyle = {} }) {
  const C = useTheme();
  const base = { primary: { background: C.accent, color: C.bg, border: "none" }, ghost: { background: "transparent", color: C.textMid, border: `1px solid ${C.border}` }, danger: { background: "rgba(255,68,68,0.1)", color: C.danger, border: `1px solid ${C.danger}` }, success: { background: "rgba(0,230,118,0.1)", color: C.success, border: `1px solid ${C.success}` }, subtle: { background: C.surfaceAlt, color: C.textMid, border: `1px solid ${C.border}` } }[variant];
  return (
    <button onClick={onClick} disabled={disabled} style={{ ...base, padding: small ? "5px 12px" : "9px 18px", borderRadius: 7, cursor: disabled ? "not-allowed" : "pointer", fontFamily: "'DM Mono', monospace", fontWeight: 600, fontSize: small ? 11 : 13, opacity: disabled ? 0.45 : 1, transition: "opacity 0.15s, transform 0.1s", letterSpacing: "0.03em", display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap", ...extraStyle }} onMouseDown={e => { if (!disabled) e.currentTarget.style.transform = "scale(0.97)"; }} onMouseUp={e => e.currentTarget.style.transform = "scale(1)"}>
      {children}
    </button>
  );
}

// ─── SIMPLE RICH TEXT TOOLBAR ──────────────────────────────────────────────
function ToolbarBtn({ label, cmd, arg, editorRef }) {
  const C = useTheme();
  return (
    <button
      onMouseDown={e => { e.preventDefault(); document.execCommand(cmd, false, arg); editorRef.current?.focus(); }}
      title={label}
      style={{ background: "transparent", border: "none", color: C.textMid, cursor: "pointer", padding: "4px 7px", borderRadius: 4, fontSize: 13, fontFamily: "serif", fontWeight: cmd === "bold" ? 700 : 400, transition: "color 0.1s" }}
      onMouseEnter={e => e.currentTarget.style.color = C.accent}
      onMouseLeave={e => e.currentTarget.style.color = C.textMid}
    >{label}</button>
  );
}

function RichEditor({ value, onChange, readOnly = false, externalRef, imperativeRef }) {
  const C = useTheme();
  const editorRef = useRef(null);
  const lastHtml = useRef(null);

  // Expose imperative setHTML so callers can bypass the lastHtml guard
  if (imperativeRef) {
    imperativeRef.current = {
      setHTML(html) {
        if (!editorRef.current) return;
        editorRef.current.innerHTML = html;
        lastHtml.current = html;
        onChange(html);
      },
    };
  }

  useEffect(() => {
    if (editorRef.current && value !== lastHtml.current) {
      editorRef.current.innerHTML = value || "";
      lastHtml.current = value;
    }
  }, [value]);

  function handleInput() {
    const html = editorRef.current.innerHTML;
    lastHtml.current = html;
    onChange(html);
  }

  const tools = [
    { label: "B", cmd: "bold" }, { label: "I", cmd: "italic" }, { label: "U", cmd: "underline" },
    { label: "H1", cmd: "formatBlock", arg: "h2" }, { label: "H2", cmd: "formatBlock", arg: "h3" },
    { label: "¶", cmd: "formatBlock", arg: "p" },
    { label: "• List", cmd: "insertUnorderedList" }, { label: "1. List", cmd: "insertOrderedList" },
    { label: "——", cmd: "insertHorizontalRule" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      {!readOnly && (
        <div style={{ display: "flex", alignItems: "center", gap: 2, padding: "6px 12px", borderBottom: `1px solid ${C.border}`, flexWrap: "wrap", background: C.surfaceAlt, borderRadius: "8px 8px 0 0" }}>
          {tools.map(t => <ToolbarBtn key={t.label} label={t.label} cmd={t.cmd} arg={t.arg} editorRef={editorRef} />)}
        </div>
      )}
      <div
        ref={node => { editorRef.current = node; if (externalRef) externalRef.current = node; }}
        contentEditable={!readOnly}
        suppressContentEditableWarning
        onInput={handleInput}
        style={{
          flex: 1, padding: "20px 28px", outline: "none", overflowY: "auto",
          color: C.text, fontSize: 15, lineHeight: 1.8, fontFamily: "'DM Mono', monospace",
          background: "transparent", minHeight: 300,
          caretColor: C.accent,
        }}
      />
      <style>{`
        [contenteditable] h2 { color: ${C.accent}; font-family: 'Syne', sans-serif; font-size: 20px; margin: 24px 0 8px; }
        [contenteditable] h3 { color: ${C.text}; font-family: 'Syne', sans-serif; font-size: 16px; margin: 18px 0 6px; }
        [contenteditable] p  { margin: 6px 0; color: ${C.textMid}; }
        [contenteditable] ul, [contenteditable] ol { color: ${C.textMid}; padding-left: 22px; }
        [contenteditable] li { margin: 4px 0; }
        [contenteditable] hr { border: none; border-top: 1px solid ${C.border}; margin: 18px 0; }
        [contenteditable]:empty:before { content: attr(data-placeholder); color: ${C.textDim}; pointer-events: none; }
      `}</style>
    </div>
  );
}

// ─── AI PANEL ─────────────────────────────────────────────────────────────────
function AIPanel({ docContent, docTitle, onApplySuggestion }) {
  const C = useTheme();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("chat"); // chat | review
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const quickActions = [
    { label: "Full Policy Review", prompt: "Please do a thorough review of this policy. Identify any gaps, weaknesses, or areas that don't align with FATF recommendations or best practices. Be specific." },
    { label: "Check FATF Alignment", prompt: "Check this document against the FATF 40 Recommendations. Which recommendations are covered? Which are missing or weak?" },
    { label: "Improve Language", prompt: "Rewrite unclear or weak sections of this policy to be more precise and regulatory-grade. Show me the improved version." },
    { label: "Find Regulatory Gaps", prompt: "What regulatory obligations under FCA, FinCEN, or FATF are NOT covered by this policy? List them specifically." },
    { label: "Suggest New Sections", prompt: "What additional sections or clauses should be added to this policy to make it more comprehensive? Draft them." },
    { label: "✦ Rewrite Full Document", prompt: `Please rewrite this entire policy document to be comprehensive, regulatory-grade, and fully aligned with FATF 40 Recommendations, FCA JMLSG, and EU AMLD best practices. Preserve the document's purpose but improve structure, language, and coverage. Output only the rewritten policy text — no preamble, no commentary.`, isRewrite: true },
  ];

  async function send(customPrompt) {
    const userMsg = customPrompt || input.trim();
    if (!userMsg || loading) return;
    setInput("");

    const contextMsg = docContent
      ? `Here is the document I'm working on titled "${docTitle}":\n\n${docContent.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()}\n\n---\n\n${userMsg}`
      : userMsg;

    setMessages(prev => [...prev, { role: "user", content: userMsg }]);
    setLoading(true);

    try {
      const res = await fetch(`${import.meta.env.VITE_PROXY_URL || "http://127.0.0.1:3001"}/api/ai`, {
        method: "POST",
        signal: AbortSignal.timeout(90000),
        headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
        body: JSON.stringify({
          model: "claude-sonnet-4-6",
          max_tokens: 2000,
          system: `You are an expert AML/CFT compliance AI assistant embedded in a compliance document management system. You help compliance officers and MLROs draft, review, and improve AML policies and procedures.

Your expertise covers: FATF 40 Recommendations, FinCEN guidelines, FCA rules (SYSC, JMLSG), MAS MAS-NOTice, EU AMLD (4th, 5th, 6th), KYC/CDD/EDD, PEP screening, sanctions, transaction monitoring, SAR filing, and governance frameworks.

When reviewing documents:
- Be specific about which regulation or recommendation each gap relates to
- Quote or reference specific FATF recommendations, FCA rules, or JMLSG paragraphs where relevant
- Provide concrete, actionable improvements
- When drafting text, write it in formal, regulatory-grade English suitable for direct inclusion in a policy document

Format your responses clearly. Use headers when helpful. When you draft text to be inserted into a policy, clearly mark it as a draft section.`,
          messages: messages.concat({ role: "user", content: contextMsg }),
        }),
      });
      const data = await res.json();
      const reply = data.content?.map(c => c.text || "").join("") || "Sorry, I couldn't get a response.";
      setMessages(prev => [...prev, { role: "assistant", content: reply }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Error connecting to AI. Check your API key in .env" }]);
    }
    setLoading(false);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, background: C.surface, borderLeft: `1px solid ${C.border}` }}>
      {/* Header */}
      <div style={{ padding: "16px 18px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ color: C.accent, fontSize: 16 }}>✦</span>
        <span style={{ color: C.text, fontWeight: 600, fontSize: 14, fontFamily: "'Syne', sans-serif" }}>AI Assistant</span>
        <span style={{ marginLeft: "auto", color: C.textDim, fontSize: 11 }}>Claude-powered</span>
      </div>

      {/* Quick actions */}
      <div style={{ padding: "12px 14px", borderBottom: `1px solid ${C.border}`, display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.1em", marginBottom: 4 }}>QUICK ACTIONS</div>
        {quickActions.map(a => (
          <button key={a.label} onClick={() => send(a.prompt)} style={{ textAlign: "left", padding: "7px 10px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMid, fontSize: 12, cursor: "pointer", fontFamily: "'DM Mono', monospace", transition: "all 0.12s" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = C.accent; e.currentTarget.style.color = C.accent; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.textMid; }}>
            {a.label} →
          </button>
        ))}
      </div>

      {/* Chat messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: "14px", display: "flex", flexDirection: "column", gap: 12 }}>
        {messages.length === 0 && (
          <div style={{ color: C.textDim, fontSize: 13, textAlign: "center", marginTop: 20, lineHeight: 1.7 }}>
            Ask me anything about this document, or use a quick action above to get a full review.
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: m.role === "user" ? "flex-end" : "flex-start" }}>
            <div style={{ fontSize: 10, color: C.textDim, fontFamily: "'DM Mono', monospace", marginBottom: 2 }}>
              {m.role === "user" ? "YOU" : "✦ AI"}
            </div>
            <div style={{
              maxWidth: "90%", padding: "10px 13px", borderRadius: m.role === "user" ? "10px 10px 2px 10px" : "10px 10px 10px 2px",
              background: m.role === "user" ? C.accentGlow : C.bg,
              border: `1px solid ${m.role === "user" ? C.borderLight : C.border}`,
              color: C.text, fontSize: 13, lineHeight: 1.7,
              whiteSpace: "pre-wrap", wordBreak: "break-word",
            }}>
              {m.content}
            </div>
            {m.role === "assistant" && onApplySuggestion && m.content.length > 150 && (
              <div style={{ display: "flex", gap: 6, marginTop: 2 }}>
                <button
                  onClick={() => {
                    const asHtml = m.content
                      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
                      .split(/\n\n+/)
                      .map(para => `<p>${para.replace(/\n/g, "<br>")}</p>`)
                      .join("");
                    onApplySuggestion((docContent || "") + "<hr style='margin:16px 0;opacity:0.25'>" + asHtml);
                  }}
                  style={{ padding: "4px 10px", fontSize: 10, fontFamily: "'DM Mono', monospace",
                    background: `${C.accent}18`, border: `1px solid ${C.accent}55`,
                    borderRadius: 5, color: C.accent, cursor: "pointer", fontWeight: 700 }}>
                  + Append to Document
                </button>
                <button
                  onClick={() => {
                    if (window.confirm("Replace the entire document content with this AI response?")) {
                      const asHtml = m.content
                        .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
                        .split(/\n\n+/)
                        .map(para => `<p>${para.replace(/\n/g, "<br>")}</p>`)
                        .join("");
                      onApplySuggestion(asHtml);
                    }
                  }}
                  style={{ padding: "4px 10px", fontSize: 10, fontFamily: "'DM Mono', monospace",
                    background: C.bg, border: `1px solid ${C.border}`,
                    borderRadius: 5, color: C.textMid, cursor: "pointer" }}>
                  ↺ Replace Document
                </button>
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div style={{ display: "flex", gap: 5, padding: "8px 4px" }}>
            {[0,1,2].map(i => <div key={i} style={{ width: 7, height: 7, borderRadius: "50%", background: C.accent, animation: `aipulse 1.2s ${i*0.2}s infinite ease-in-out` }} />)}
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Input */}
      <div style={{ padding: "12px 14px", borderTop: `1px solid ${C.border}`, display: "flex", gap: 8 }}>
        <textarea
          value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Ask about this document..."
          style={{ flex: 1, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 13, padding: "8px 11px", fontFamily: "'DM Mono', monospace", resize: "none", outline: "none", minHeight: 38, maxHeight: 100, lineHeight: 1.5 }}
          rows={1}
        />
        <button onClick={() => send()} disabled={!input.trim() || loading} style={{ padding: "8px 13px", background: input.trim() ? C.accent : C.border, border: "none", borderRadius: 7, color: C.bg, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 12, flexShrink: 0 }}>
          Send
        </button>
      </div>
      <style>{`@keyframes aipulse { 0%,80%,100%{opacity:0.2;transform:scale(0.7)} 40%{opacity:1;transform:scale(1)} }`}</style>
    </div>
  );
}

// ─── DOCUMENT EDITOR ──────────────────────────────────────────────────────────
function DocumentEditor({ doc, onSave, onClose, currentUser, team, company }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [title, setTitle] = useState(doc.title || "");
  const [content, setContent] = useState(doc.content || "");
  const [category, setCategory] = useState(doc.category || "Core");
  const [status, setStatus] = useState(doc.status || "draft");
  const [showAI, setShowAI] = useState(false);
  const [saved, setSaved] = useState(false);
  const editorDomRef = useRef(null);
  const richEditorRef = useRef(null);
  const [richEditorKey, setRichEditorKey] = useState(0);
  const [showAssign, setShowAssign] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifCount, setNotifCount] = useState(() => getPolicyNotifications().filter(n => n.toId === currentUser.id && !n.read).length);

  // Refresh unread count when panel closes
  function closeNotifs() {
    setShowNotifs(false);
    setNotifCount(getPolicyNotifications().filter(n => n.toId === currentUser.id && !n.read).length);
  }

  function handleAssigned(updatedDoc) {
    onSave(updatedDoc);
    setStatus(updatedDoc.status);
  }

  // statusOverride lets approve/reject buttons pass new status directly,
  // avoiding a React state closure race condition with setStatus + setTimeout.
  function save(statusOverride) {
    const today = new Date().toISOString().slice(0, 10);
    const docVersion = String(doc.version || 'v1.0');
    const newVersion = bumpVersion(docVersion);
    const resolvedStatus = statusOverride !== undefined ? statusOverride : status;
    // E-026: version history — keep last 20 versions
    const prevEntry = {
      version: String(doc.version || ''),
      status: String(doc.status || ''),
      updated: String(doc.updated || ''),
      updatedBy: String(doc.updatedBy || ''),
      savedAt: new Date().toISOString()
    };
    const versionHistory = [...(doc.versionHistory || []), prevEntry].slice(-20);
    // E-028: set next annual review date
    const nextReviewDate = new Date(Date.now() + POLICY_REVIEW_DAYS * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    if (statusOverride !== undefined) setStatus(statusOverride);
    onSave({
      ...doc,
      title: String(title || ''),
      content: String(content || ''),
      category: String(category || 'Core'),
      status: String(resolvedStatus || 'draft'),
      version: String(newVersion || 'v1.0'),
      updated: String(today || ''),
      updatedBy: `${currentUser.firstName} ${currentUser.lastName}`,
      versionHistory,
      next_review_date: nextReviewDate
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  function bumpVersion(v) {
    const vStr = String(v || '');
    if (!vStr) return "v1.0";
    const match = vStr.match(/v(\d+)\.(\d+)/);
    if (!match) return "v1.0";
    return `v${match[1]}.${parseInt(match[2]) + 1}`;
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: C.bg, zIndex: 100, display: "flex", flexDirection: "column", fontFamily: "'DM Mono', monospace" }}>
      <link href="https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Mono:wght@300;400;500&display=swap" rel="stylesheet" />
      {showAssign && <AssignReviewModal doc={{ ...doc, title, content, status }} team={team} currentUser={currentUser} company={company} onClose={() => setShowAssign(false)} onAssigned={handleAssigned} />}
      {showNotifs && <NotificationsPanel currentUser={currentUser} onClose={closeNotifs} />}

      {/* Top bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 20px", background: C.surface, borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
        <button onClick={onClose} style={{ background: "transparent", border: "none", color: C.textMid, cursor: "pointer", fontSize: 18, padding: "2px 6px", borderRadius: 4 }}>←</button>
        <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Document title..." style={{ flex: 1, background: "transparent", border: "none", color: C.text, fontSize: 17, fontFamily: "'Syne', sans-serif", fontWeight: 700, outline: "none" }} />

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <select value={category} onChange={e => setCategory(e.target.value)} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMid, fontSize: 12, padding: "5px 10px", fontFamily: "'DM Mono', monospace", outline: "none" }}>
            {CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
          <select value={status} onChange={e => setStatus(e.target.value)} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMid, fontSize: 12, padding: "5px 10px", fontFamily: "'DM Mono', monospace", outline: "none" }}>
            {STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
          <Btn small onClick={() => setShowAI(v => !v)} variant={showAI ? "primary" : "ghost"}>✦ AI {showAI ? t('common.hide') : t('policies.review')}</Btn>
          <Btn small onClick={() => setShowAssign(true)} variant="ghost">
            ↗ {t('policies.assignForReview')}
          </Btn>
          <button onClick={() => { setShowNotifs(v => !v); }} style={{ position: "relative", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 7, color: C.textMid, cursor: "pointer", padding: "5px 10px", fontFamily: "'DM Mono', monospace", fontSize: 12 }}>
            🔔
            {notifCount > 0 && <span style={{ position: "absolute", top: -5, right: -5, background: C.danger, color: "#fff", fontSize: 9, padding: "1px 5px", borderRadius: 10, fontWeight: 700, lineHeight: 1.5 }}>{notifCount}</span>}
          </button>
          {/* E-027: Submit for Approval workflow */}
          {status === "review" && (
            <Btn small variant="ghost" onClick={() => save("pending_approval")}>
              ↗ Submit for Approval
            </Btn>
          )}
          {status === "pending_approval" && (
            currentUser?.permission === "admin" ||
            currentUser?.permission === "co" ||
            currentUser?.permission === "compliance_officer"
          ) && (
            <>
              <Btn small variant="success" onClick={() => save("approved")}>✓ Approve</Btn>
              <Btn small variant="danger" onClick={() => save("draft")}>✕ Reject</Btn>
            </>
          )}
          {/* General save button for any status changes */}
          <Btn small variant="primary" onClick={() => save()}>
            {saved ? "✓ Saved" : "💾 Save"}
          </Btn>
          <Btn small onClick={() => {
            printToPDF(`${title || "Policy"} — ${status?.toUpperCase()}`, `
              <div class="section">
                <div class="section-title">Document Details</div>
                <table><tbody>
                  <tr><td style="width:30%;color:#4A5568">Title</td><td><strong>${title || "Untitled"}</strong></td></tr>
                  <tr><td style="color:#4A5568">Category</td><td>${category || "—"}</td></tr>
                  <tr><td style="color:#4A5568">Version</td><td>${doc.version || "v1.0"}</td></tr>
                  <tr><td style="color:#4A5568">Status</td><td style="text-transform:uppercase;font-weight:700">${status || "draft"}</td></tr>
                  <tr><td style="color:#4A5568">Last Updated</td><td>${doc.updated || "—"}</td></tr>
                  <tr><td style="color:#4A5568">Updated By</td><td>${doc.updatedBy || "—"}</td></tr>
                  ${doc.next_review_date ? `<tr><td style="color:#4A5568">Next Review</td><td>${doc.next_review_date}</td></tr>` : ""}
                </tbody></table>
              </div>
              <div class="section">
                <div class="section-title">Policy Content</div>
                <div style="font-size:11px;line-height:1.8">${content || "<em>No content.</em>"}</div>
              </div>
              ${doc.versionHistory?.length ? `<div class="section">
                <div class="section-title">Version History</div>
                <table><thead><tr><th>Version</th><th>Status</th><th>Date</th><th>By</th></tr></thead><tbody>
                  ${[...doc.versionHistory].reverse().slice(0,10).map(v =>
                    `<tr><td>${v.version||"—"}</td><td>${v.status||"—"}</td><td>${v.updated||"—"}</td><td>${v.updatedBy||"—"}</td></tr>`
                  ).join("")}
                </tbody></table>
              </div>` : ""}
            `, company?.name || "CrossRegGuard");
          }} variant="ghost" small>⬇ PDF</Btn>
          <Btn small onClick={save} variant="success">{saved ? `✓ ${t('common.saved')}` : t('common.save')}</Btn>
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, display: "grid", gridTemplateColumns: showAI ? "1fr 340px" : "1fr", minHeight: 0, overflow: "hidden" }}>
        <div style={{ display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
          <RichEditor key={richEditorKey} value={content} onChange={setContent} externalRef={editorDomRef} />
        </div>
        {showAI && (
          <div style={{ minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <AIPanel
            docContent={content}
            docTitle={title}
            onApplySuggestion={(htmlOrFn) => {
              const current = editorDomRef.current?.innerHTML ?? content;
              const next = typeof htmlOrFn === "function" ? htmlOrFn(current) : htmlOrFn;
              setContent(next);
              setRichEditorKey(k => k + 1);
            }}
          />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── UPLOAD MODAL ─────────────────────────────────────────────────────────────
function UploadModal({ onUpload, onClose, currentUser }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [dragging, setDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Core");
  const [extracting, setExtracting] = useState(false);
  const [extracted, setExtracted] = useState("");
  const inputRef = useRef();

  function handleFile(f) {
    if (!f) return;
    setFile(f);
    setTitle(f.name.replace(/\.[^/.]+$/, ""));
    if (f.type === "text/plain") {
      const reader = new FileReader();
      reader.onload = e => setExtracted(e.target.result);
      reader.readAsText(f);
    } else if (f.type === "application/pdf" || f.name.endsWith(".docx")) {
      const docType = f.type === "application/pdf" ? "PDF" : "Word";
      setExtracted(`📄 ${docType} Document: ${f.name}\n\nFile has been uploaded and is ready to edit. You can now:\n• Edit the document content directly\n• Add your own text summary\n• Set the status and version\n• Save and share with your team`);
    }
  }

  function generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  function doUpload() {
    if (!file) return;
    const extractedText = typeof extracted === 'string' ? extracted : '';
    onUpload({
      id: generateUUID(),
      title: title || (file.name ? String(file.name) : 'Document'),
      content: extractedText ? `<p>${extractedText.replace(/\n\n/g, "</p><p>").replace(/\n/g, "<br/>")}</p>` : "",
      category,
      status: "draft",
      version: "v1.0",
      updated: new Date().toISOString().slice(0, 10),
      updatedBy: `${currentUser.firstName || 'Unknown'} ${currentUser.lastName || 'User'}`,
      uploaded: true,
      fileName: file.name ? String(file.name) : '',
    });
    onClose();
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 32, width: 480, fontFamily: "'DM Mono', monospace" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 22 }}>
          <div style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: 18, fontWeight: 700 }}>{t('policies.uploadDocument')}</div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: C.textDim, cursor: "pointer", fontSize: 18 }}>✕</button>
        </div>

        {/* Drop zone */}
        <div
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
          onClick={() => inputRef.current.click()}
          style={{
            border: `2px dashed ${dragging ? C.accent : file ? C.success : C.border}`,
            borderRadius: 10, padding: "28px 20px", textAlign: "center", cursor: "pointer",
            background: dragging ? C.accentGlow : "transparent", marginBottom: 18,
            transition: "all 0.15s",
          }}
        >
          <input ref={inputRef} type="file" accept=".txt,.pdf,.docx,.md" style={{ display: "none" }} onChange={e => handleFile(e.target.files[0])} />
          {file ? (
            <div>
              <div style={{ fontSize: 28, marginBottom: 6 }}>📄</div>
              <div style={{ color: C.success, fontSize: 13 }}>{file.name}</div>
              <div style={{ color: C.textDim, fontSize: 11, marginTop: 4 }}>{(file.size / 1024).toFixed(1)} KB</div>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 28, marginBottom: 8 }}>⬆</div>
              <div style={{ color: C.textMid, fontSize: 14 }}>{t('policies.dropFileHere')}</div>
              <div style={{ color: C.textDim, fontSize: 11, marginTop: 6 }}>{t('policies.fileTypes')}</div>
            </div>
          )}
        </div>

        {/* Metadata */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
          <div>
            <div style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.08em", marginBottom: 6 }}>{t('policies.documentTitle')}</div>
            <input value={title} onChange={e => setTitle(e.target.value)} style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 13, padding: "9px 12px", fontFamily: "'DM Mono', monospace", outline: "none", boxSizing: "border-box" }} />
          </div>
          <div>
            <div style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.08em", marginBottom: 6 }}>{t('policies.category')}</div>
            <select value={category} onChange={e => setCategory(e.target.value)} style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 13, padding: "9px 12px", fontFamily: "'DM Mono', monospace", outline: "none" }}>
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <Btn variant="ghost" onClick={onClose}>{t('common.cancel')}</Btn>
          <Btn onClick={doUpload} disabled={!file}>{t('policies.uploadAndOpen')}</Btn>
        </div>
      </div>
    </div>
  );
}

// ─── NEW DOC MODAL ────────────────────────────────────────────────────────────
function NewDocModal({ onClose, onCreate, currentUser }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Core");
  const [aiGenerate, setAiGenerate] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [generating, setGenerating] = useState(false);

  function generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  const templates = [
    { label: "AML/CFT Policy", prompt: "Draft a comprehensive AML/CFT Policy for a regulated financial institution. Include: purpose & scope, risk-based approach, customer due diligence, enhanced due diligence, ongoing monitoring, suspicious activity reporting, record-keeping, training, and governance. Use formal regulatory language." },
    { label: "KYC Procedure", prompt: "Draft a detailed KYC (Know Your Customer) procedure document. Cover: customer identification, verification methods, beneficial ownership, politically exposed persons (PEPs), reliance on third parties, and documentation requirements. Align with FATF Recommendations 10-12." },
    { label: "SAR Filing Procedure", prompt: "Draft a Suspicious Activity Report (SAR) filing procedure. Include: triggers for suspicion, internal reporting process, MLRO review, external filing with the FIU, tipping-off prohibition, record-keeping, and staff responsibilities." },
    { label: "Sanctions Policy", prompt: "Draft a Sanctions Screening Policy covering: screening obligations, systems and processes, OFAC/UN/EU/HMT lists, screening frequency, alert handling, escalation, and governance. Reference relevant regulatory frameworks." },
    { label: "EDD Procedure", prompt: "Draft an Enhanced Due Diligence (EDD) procedure for high-risk customers. Include: EDD triggers, additional information requirements, senior management approval, ongoing enhanced monitoring, and documentation standards." },
    { label: "Blank Document", prompt: "" },
  ];

  async function create(tmpl) {
    const docTitle = title || tmpl.label;
    const docId = generateUUID();
    if (!tmpl.prompt) {
      onCreate({ id: docId, title: docTitle, content: "", category, status: "draft", version: "v1.0", updated: new Date().toISOString().slice(0, 10), updatedBy: `${currentUser.firstName} ${currentUser.lastName}` });
      onClose();
      return;
    }
    setGenerating(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_PROXY_URL || "http://127.0.0.1:3001"}/api/ai`, {
        method: "POST",
        signal: AbortSignal.timeout(90000),
        headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
        body: JSON.stringify({
          model: "claude-sonnet-4-6", max_tokens: 3000,
          system: "You are an expert AML compliance officer drafting regulatory documents. Produce well-structured, professional policy documents in HTML format using only <h2>, <h3>, <p>, <ul>, <li>, <ol>, <strong>, <em> tags. No markdown. No code blocks. Return only the HTML content of the document body.",
          messages: [{ role: "user", content: `${tmpl.prompt}\n\nDocument title: "${docTitle}"\n\nReturn the complete document as HTML.` }],
        }),
      });
      const data = await res.json();
      const html = data.content?.map(c => c.text || "").join("") || "<p>Document generation failed. Please try again.</p>";
      onCreate({ id: docId, title: docTitle, content: html, category, status: "draft", version: "v1.0", updated: new Date().toISOString().slice(0, 10), updatedBy: `${currentUser.firstName} ${currentUser.lastName}` });
    } catch {
      onCreate({ id: docId, title: docTitle, content: "<p>Generation failed — please check your API key.</p>", category, status: "draft", version: "v1.0", updated: new Date().toISOString().slice(0, 10), updatedBy: `${currentUser.firstName} ${currentUser.lastName}` });
    }
    setGenerating(false);
    onClose();
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 32, width: 560, fontFamily: "'DM Mono', monospace" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
          <div style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: 18, fontWeight: 700 }}>{t('policies.newDocument')}</div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: C.textDim, cursor: "pointer", fontSize: 18 }}>✕</button>
        </div>
        <p style={{ color: C.textMid, fontSize: 13, margin: "0 0 20px" }}>{t('policies.newDocDesc')}</p>

        {generating ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <div style={{ display: "flex", gap: 8, justifyContent: "center", marginBottom: 16 }}>
              {[0,1,2].map(i => <div key={i} style={{ width: 10, height: 10, borderRadius: "50%", background: C.accent, animation: `aipulse 1.2s ${i*0.2}s infinite` }} />)}
            </div>
            <div style={{ color: C.textMid, fontSize: 14 }}>{t('policies.aiDrafting')}</div>
            <div style={{ color: C.textDim, fontSize: 12, marginTop: 6 }}>{t('policies.aiDraftingWait')}</div>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 16 }}>
              <div style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.08em", marginBottom: 8 }}>{t('policies.documentTitleOptional')}</div>
              <input value={title} onChange={e => setTitle(e.target.value)} placeholder={t('policies.titleTemplatePlaceholder')} style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 13, padding: "9px 12px", fontFamily: "'DM Mono', monospace", outline: "none", boxSizing: "border-box" }} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {templates.map(tmpl => (
                <button key={tmpl.label} onClick={() => create(tmpl)} style={{
                  padding: "14px 16px", background: C.bg, border: `1px solid ${C.border}`,
                  borderRadius: 9, cursor: "pointer", textAlign: "left", transition: "all 0.13s",
                  fontFamily: "'DM Mono', monospace",
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = C.accent; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; }}>
                  <div style={{ color: C.text, fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{tmpl.label}</div>
                  <div style={{ color: tmpl.prompt ? C.accent : C.textDim, fontSize: 10 }}>{tmpl.prompt ? `✦ ${t('policies.aiGenerated')}` : t('policies.blankCanvas')}</div>
                </button>
              ))}
            </div>
          </>
        )}
        <style>{`@keyframes aipulse { 0%,80%,100%{opacity:0.2;transform:scale(0.7)} 40%{opacity:1;transform:scale(1)} }`}</style>
      </div>
    </div>
  );
}

// ─── NOTIF BELL (list view) ──────────────────────────────────────────────────
function NotifBell({ currentUser }) {
  const C = useTheme();
  const [show, setShow] = useState(false);
  const [count, setCount] = useState(() => getPolicyNotifications().filter(n => n.toId === currentUser.id && !n.read).length);

  function toggle() {
    setShow(v => !v);
    if (show) setCount(getPolicyNotifications().filter(n => n.toId === currentUser.id && !n.read).length);
  }

  return (
    <>
      <button onClick={toggle} style={{ position: "relative", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 7, color: C.textMid, cursor: "pointer", padding: "7px 11px", fontFamily: "'DM Mono', monospace", fontSize: 14 }}>
        🔔
        {count > 0 && <span style={{ position: "absolute", top: -5, right: -5, background: C.danger, color: "#fff", fontSize: 9, padding: "1px 5px", borderRadius: 10, fontWeight: 700, lineHeight: 1.5 }}>{count}</span>}
      </button>
      {show && <NotificationsPanel currentUser={currentUser} onClose={() => { setShow(false); setCount(getPolicyNotifications().filter(n => n.toId === currentUser.id && !n.read).length); }} />}
    </>
  );
}

// ─── REGULATORY GUIDELINES DATA ───────────────────────────────────────────────
const REGULATORY_GUIDELINES = [
  {
    id: "fincen",
    regulator: "FinCEN",
    fullName: "Financial Crimes Enforcement Network",
    jurisdiction: "United States",
    flag: "🇺🇸",
    accent: "#3B82F6",
    framework: "Bank Secrecy Act (BSA) · USA PATRIOT Act · 31 CFR Chapter X",
    keyLaws: ["Bank Secrecy Act 1970", "USA PATRIOT Act 2001", "AML Act 2020", "CDD Rule 2016"],
    sections: [
      {
        title: "AML Program — Four Pillars (31 CFR 1020.210)",
        items: [
          "Internal controls: written policies and procedures addressing all BSA/AML obligations, approved by senior management",
          "Independent testing: annual AML audit by qualified, independent party (internal audit or external firm)",
          "BSA Compliance Officer: designated individual responsible for day-to-day BSA compliance, must have authority and resources",
          "Ongoing training: all applicable employees must receive AML training commensurate with their roles and risk exposure",
          "Fifth pillar (2016 CDD Rule): customer due diligence procedures to identify and verify beneficial owners",
        ],
      },
      {
        title: "Customer Identification Program (CIP) — 31 CFR 1020.220",
        items: [
          "Required information (individuals): legal name, date of birth, residential address, government ID number (SSN/TIN or passport/alien ID)",
          "Required information (entities): legal name, address, EIN or comparable number",
          "Verification: documentary (government-issued photo ID) or non-documentary (credit bureaus, public databases, anti-fraud services)",
          "Must verify before or within a reasonable time of account opening",
          "Retention: 5 years after account closure for identifying information; 5 years from record creation for verification records",
          "Notice: must provide customers notice that identity is being verified",
        ],
      },
      {
        title: "Customer Due Diligence — Beneficial Ownership (CDD Rule)",
        items: [
          "Beneficial ownership: identify and verify all individuals owning ≥ 25% equity interest in a legal entity customer",
          "Control person: identify and verify one individual with significant responsibility to control, manage, or direct the entity",
          "Use FinCEN's standard Beneficial Ownership Certification Form or equivalent",
          "Ongoing monitoring: update CDD information when material risks change or at routine review intervals",
          "Exclusions: sole proprietorships, unincorporated associations, natural persons (apply CIP instead)",
        ],
      },
      {
        title: "Currency Transaction Reports (CTR) — 31 CFR 1010.311",
        items: [
          "Report all cash transactions exceeding $10,000 in a single business day",
          "Aggregation rule: multiple same-day cash transactions by or for the same person must be aggregated",
          "File with FinCEN within 15 calendar days of the transaction",
          "Structuring (breaking transactions to avoid CTR reporting) is a federal crime under 31 USC 5324",
          "CTR exemptions available for Phase I (banks, government agencies, listed companies) and Phase II (established businesses) under 31 CFR 1020.315",
          "Retain CTRs and supporting records for 5 years",
        ],
      },
      {
        title: "Suspicious Activity Reports (SAR) — 31 CFR 1020.320",
        items: [
          "Banks: file SAR for transactions ≥ $5,000 involving known, suspected, or attempted criminal activity",
          "MSBs and non-bank financial institutions: threshold is $2,000",
          "File with FinCEN within 30 days of detection; 60 days if no suspect is identified",
          "Tipping-off prohibition: do not disclose SAR filing or investigation to the subject (18 USC 1960)",
          "Safe harbour: good-faith SAR filers are protected from civil liability",
          "Retain SARs and supporting documentation for 5 years from the date of filing",
          "Continuing SAR: file follow-up SARs every 90 days if suspicious activity continues",
        ],
      },
      {
        title: "314(a) & 314(b) Information Sharing",
        items: [
          "314(a) — mandatory: must search records and respond to FinCEN requests naming terrorist financing or money laundering subjects within 14 days",
          "314(b) — voluntary: financial institutions may share information with each other for AML purposes after notifying FinCEN; provides safe harbour from privacy laws",
        ],
      },
      {
        title: "Record Keeping Requirements",
        items: [
          "BSA records (CTRs, SARs, CIP): 5 years",
          "Funds transfer records for transfers ≥ $3,000 (Recordkeeping Rule): 5 years",
          "Purchase of monetary instruments ≥ $3,000: purchaser identity, amount, type: 5 years",
          "All records must be retrievable within a reasonable time and made available to regulators on request",
          "Foreign correspondent account records (Patriot Act 319): may be required to be produced within 7 days",
        ],
      },
      {
        title: "Virtual Asset Service Provider (VASP) Requirements",
        items: [
          "MSB registration: VASPs (crypto exchanges, administrators, exchangers) are Money Services Businesses and must register with FinCEN before operating",
          "BSA applies in full: all four AML programme pillars, CIP, CDD, SAR, and CTR obligations apply to VASPs as they do to traditional MSBs",
          "Travel Rule (Funds Transfer Rule, 31 CFR 1010.410): for virtual asset transfers of $3,000 or more, transmitting institutions must collect and pass originator and beneficiary name, account, and address to the receiving institution",
          "FinCEN 2019 Guidance (FIN-2019-G001): clarifies that convertible virtual currency (CVC) is 'funds' under the BSA; unhosted wallet transactions, P2P exchanges, and DeFi platforms may trigger MSB registration",
          "SAR threshold for VASPs: $2,000 (MSB threshold) — file within 30 days of detection",
          "OFAC sanctions screening: screen all customers and counterparties against OFAC SDN list; crypto mixing, ransomware payments, and transactions with sanctioned exchanges are high-risk indicators",
          "2021 Proposed Rule (unhosted wallets): FinCEN proposed requiring identification for CVC transfers to/from unhosted wallets above $3,000, and reporting for transfers above $10,000 — monitor for finalisation",
          "State licensing: VASPs must also obtain state money transmitter licences (MTL) in each state where they operate — requirements vary by state",
        ],
      },
      {
        title: "Travel Rule — Funds Transfer Rule (31 CFR 1010.410)",
        items: [
          "Threshold: $3,000 USD equivalent for virtual asset transfers between financial institutions; below this threshold, VASPs must still collect and retain the information but need not transmit it",
          "Required originator information to transmit: name, account number (wallet address), and physical address OR national ID number OR date and place of birth",
          "Required beneficiary information: name and account number (wallet address) must be transmitted to the receiving institution",
          "Counterparty VASP due diligence: transmitting institutions must take reasonable steps to verify the receiving institution has a Travel Rule compliance programme before sending Travel Rule data",
          "Receiving institution obligations: must retain all incoming originator information for 5 years and make it available to regulators on request",
          "Sunrise problem — FinCEN approach: where a counterparty VASP lacks the technical capability to receive Travel Rule data, apply a risk-based approach — document transmission attempts, assess counterparty risk, and retain originator data internally pending resolution",
          "Unhosted wallet proposed rule: FinCEN's 2021 proposed rule would require identification and CTR-style reporting for unhosted wallet transactions above $10,000 — monitor for finalisation",
          "FinCEN ANPRM (2023): FinCEN has signaled intention to lower the $3,000 threshold to $250 for international transfers and align with the FATF $1,000 standard — monitor for final rulemaking",
          "Enforcement precedent: FinCEN and OFAC have jointly issued significant penalties for Travel Rule non-compliance — BitMEX ($100M+), Bitfinex ($24M), and Binance ($4.3B) were fined in part for Travel Rule failures",
          "Record retention: all Travel Rule records (originator and beneficiary data, transmission records) must be retained for 5 years from the date of the transaction",
        ],
      },
    ],
  },
  {
    id: "fintrac",
    regulator: "FINTRAC",
    fullName: "Financial Transactions and Reports Analysis Centre of Canada",
    jurisdiction: "Canada",
    flag: "🇨🇦",
    accent: "#DC2626",
    framework: "PCMLTFA · PCMLTFR · FINTRAC Guidance",
    keyLaws: ["Proceeds of Crime (Money Laundering) and Terrorist Financing Act (PCMLTFA)", "PCMLTF Regulations", "FINTRAC Guidelines 2023"],
    sections: [
      {
        title: "Compliance Program Requirements (PCMLTFA s.9.6)",
        items: [
          "Compliance officer: appoint a senior individual responsible for the compliance program",
          "Written policies and procedures: document all AML/ATF obligations for your reporting entity sector",
          "Risk assessment: conduct and document an entity-wide ML/TF risk assessment",
          "Ongoing training: ensure all employees are trained on AML/ATF obligations relevant to their roles",
          "Effectiveness review: conduct a two-year review of the compliance program to assess effectiveness and make improvements",
        ],
      },
      {
        title: "Client Identification",
        items: [
          "Individuals (face-to-face): verify full name and address using government-issued photo ID",
          "Individuals (non-face-to-face): use dual-process method — two independent reliable sources confirming name and address, or credit file data",
          "Entities: confirm existence using certificate of incorporation, partnership agreement, or equivalent; identify beneficial owners",
          "Beneficial ownership: identify individuals owning or controlling ≥ 25% of an entity",
          "PEPs (domestic and foreign): apply EDD, senior management approval for business relationships",
          "Timing: identify clients at account opening or before the first transaction above reporting thresholds",
        ],
      },
      {
        title: "Large Cash Transaction Report (LCTR)",
        items: [
          "Threshold: CAD $10,000 or more in cash received in a single transaction or series of related transactions",
          "File with FINTRAC within 15 calendar days of the transaction",
          "Information required: client details, conductor details, amount, currency, account information, purpose",
          "24-hour rule: transactions within a 24-hour period by or for the same person must be aggregated",
          "Structuring: transaction amounts deliberately kept below $10,000 to avoid reporting is an offence",
        ],
      },
      {
        title: "Suspicious Transaction Reports (STR)",
        items: [
          "No minimum threshold: must report any transaction — completed or attempted — if you have reasonable grounds to suspect ML/TF",
          "File with FINTRAC within 30 days of detecting suspicious behaviour",
          "Tipping-off prohibition: do not disclose to the subject that an STR has been submitted",
          "Include all relevant facts, context, and why the transaction is suspicious",
          "Record-keeping: retain all records relating to STRs for 5 years from date of filing",
        ],
      },
      {
        title: "Electronic Funds Transfer Reports (EFTR)",
        items: [
          "Threshold: international EFTs of CAD $10,000 or more (sent or received)",
          "File within 5 business days of sending or receiving the transfer",
          "Applies to swift, CHIPS, ACH, and other international electronic payment systems",
          "Aggregation: multiple EFTs to/from the same person in a 24-hour period must be aggregated",
        ],
      },
      {
        title: "Record Keeping",
        items: [
          "All AML/ATF records must be retained for 5 years from the date the record was created",
          "Records must be readily accessible and provided to FINTRAC officers on request within 30 days",
          "Client identification records, transaction records, business relationship records, STRs all subject to 5-year retention",
        ],
      },
      {
        title: "Virtual Asset Service Provider (VASP) Requirements",
        items: [
          "Registration as MSB: businesses dealing in virtual currencies (buying/selling crypto for customers, exchanging crypto-to-fiat, or transferring crypto on behalf of clients) are Money Services Businesses and must register with FINTRAC",
          "Full PCMLTFA obligations apply: KYC, record keeping, STR, and LCTR obligations apply to crypto MSBs as they do to traditional MSBs",
          "Large Virtual Currency Transaction Report (LVCTR): report virtual currency transactions of CAD $10,000 or more to FINTRAC within 10 business days",
          "Aggregation: multiple virtual currency transactions by or for the same person within a 24-hour period must be aggregated and reported if they total CAD $10,000 or more",
          "Travel Rule (FINTRAC Guidance 2023): for virtual currency transfers of CAD $1,000 or more, the sending VASP must collect and transmit originator and beneficiary name, account/wallet address, and address or ID number to the receiving VASP",
          "Unhosted wallets: where a transfer is to or from an unhosted (self-hosted) wallet, the regulated entity must collect the wallet address and apply enhanced scrutiny",
          "STR obligations: file an STR for any transaction — regardless of value — where ML/TF is suspected; crypto-specific red flags include rapid wallet cycling, mixer use, and darknet market indicators",
          "Beneficial ownership: VASPs must identify and verify beneficial owners of corporate clients operating crypto accounts",
        ],
      },
      {
        title: "Travel Rule — PCMLTFA & FINTRAC Guidance (2023 Amendments)",
        items: [
          "Threshold: CAD $1,000 or more (or equivalent in any currency or virtual currency) per transfer; effective June 2021 under the updated PCMLTF Regulations",
          "Required originator information to transmit: originator full name, account number or virtual currency address, and physical address or national ID number",
          "Required beneficiary information: beneficiary full name and account number or virtual currency address",
          "Same-day aggregation: multiple virtual currency transfers to the same beneficiary on the same calendar day must be aggregated — if the total reaches CAD $1,000, Travel Rule obligations apply to all transfers in the series",
          "Counterparty identification: before transmitting, take reasonable measures to confirm whether the receiving entity is a FINTRAC-registered VASP or an equivalent regulated entity in a foreign jurisdiction",
          "Receiving entity obligations: verify completeness of incoming Travel Rule data; retain all originator information for 5 years; conduct risk-based review of transfers with missing or inconsistent data",
          "Sunrise problem — FINTRAC approach: where a foreign counterparty cannot receive structured Travel Rule data, retain the information internally in a format that can be transmitted when the counterparty achieves compliance; document all steps taken",
          "Unhosted wallet treatment: collect the wallet address at a minimum; apply EDD for high-value or high-risk transfers involving unhosted wallets; document the ownership assessment methodology",
          "Record keeping: retain all Travel Rule records (originator data, beneficiary data, transmission logs) for 5 years from the date of the transaction; records must be retrievable within a reasonable time on FINTRAC request",
          "Interaction with LVCTR: a single virtual currency transfer that triggers both the $10,000 LVCTR threshold and the $1,000 Travel Rule threshold requires compliance with both obligations simultaneously",
        ],
      },
    ],
  },
  {
    id: "fca",
    regulator: "FCA / JMLSG",
    fullName: "Financial Conduct Authority / Joint Money Laundering Steering Group",
    jurisdiction: "United Kingdom",
    flag: "🇬🇧",
    accent: "#7C3AED",
    framework: "MLR 2017 · Proceeds of Crime Act 2002 · Terrorism Act 2000 · JMLSG Guidance",
    keyLaws: ["Money Laundering Regulations 2017 (as amended)", "Proceeds of Crime Act 2002 (POCA)", "Terrorism Act 2000", "JMLSG Guidance Parts I–III"],
    sections: [
      {
        title: "Policies, Controls and Procedures (MLR 2017, Reg. 18)",
        items: [
          "Firm-wide risk assessment: identify and assess ML/TF risks to which the firm is exposed; document and keep up to date",
          "Policies, controls and procedures: must address CDD, ongoing monitoring, reporting, record-keeping, management controls, risk management systems, internal audit",
          "MLRO: appoint a Money Laundering Reporting Officer; in FCA-regulated firms this is the SMF 17 (MLRO) senior management function",
          "Board/senior management approval: policies must be approved at board or senior management level",
          "Independent audit: compliance arrangements must be subject to independent audit (Reg. 21)",
        ],
      },
      {
        title: "Customer Due Diligence (MLR 2017, Reg. 28–36)",
        items: [
          "Standard CDD: verify name, address, and date of birth for individuals using reliable, independent documentation",
          "Entities: verify name, registered office, directors, and beneficial owners (≥ 25% ownership or control)",
          "Simplified CDD (Reg. 37): available for lower-risk customers (e.g. listed companies, UK public bodies) — reduced monitoring acceptable",
          "Enhanced Due Diligence (Reg. 33): mandatory for high-risk situations including PEPs, high-risk third countries, non-face-to-face, complex structures",
          "Ongoing monitoring (Reg. 28(11)): scrutinise transactions, keep documents current, apply CDD on trigger events",
          "Timing: CDD must be completed before establishing a business relationship or carrying out an occasional transaction",
        ],
      },
      {
        title: "Politically Exposed Persons (MLR 2017, Reg. 35)",
        items: [
          "Foreign PEPs: senior management approval required; source of wealth and source of funds must be established; enhanced ongoing monitoring",
          "Domestic PEPs and international organisation PEPs: risk-based approach since Jan 2020 — not automatically high risk",
          "Family members and known close associates (KCAs) of PEPs must also be identified and assessed",
          "PEP status must be reassessed at least 12 months after the individual leaves their prominent function",
        ],
      },
      {
        title: "Suspicious Activity Reports — POCA 2002 / Terrorism Act 2000",
        items: [
          "Internal SAR: all staff must report suspicion to the MLRO immediately — no minimum threshold",
          "MLRO review: MLRO has 7 working days to decide whether to submit an external SAR to the National Crime Agency (NCA)",
          "Defence Against Money Laundering (DAML): where a firm knows or suspects it is about to deal with criminal property, it must seek a DAML SAR and await NCA consent or expiry of 7-day moratorium (extended to 31 days if NCA objects)",
          "Tipping-off: criminal offence under POCA s.333A — do not inform the subject of an SAR or investigation",
          "Protected disclosure: good-faith SAR reporters have statutory protection from liability",
          "NCA SARs Online portal: submit all external SARs electronically",
        ],
      },
      {
        title: "High-Risk Third Countries (MLR 2017, Reg. 33)",
        items: [
          "EDD is mandatory for business relationships or transactions involving high-risk third countries identified by HM Treasury",
          "Additional measures: examine background and purpose of transactions, obtain senior management approval, conduct enhanced ongoing monitoring",
          "UK high-risk country list is published by HM Treasury and updated periodically — align with FATF blacklist and greylist",
        ],
      },
      {
        title: "Record Keeping (MLR 2017, Reg. 40)",
        items: [
          "CDD records: retain for 5 years from the end of the business relationship or occasional transaction",
          "Transaction records: retain for 5 years from the date of the transaction",
          "Do not retain records longer than necessary under UK GDPR",
          "Records must be made available to the FCA, HMRC, or NCA on request",
          "JMLSG Guidance recommends keeping a log of all internal SARs, MLRO decisions, and external filings",
        ],
      },
      {
        title: "Virtual Asset Service Provider (VASP) Requirements",
        items: [
          "FCA registration (MLR 2017, Reg. 14A): all UK crypto asset businesses must register with the FCA under the MLR 2017 cryptoasset regime before conducting any regulated activity — operating without registration is a criminal offence",
          "JMLSG Guidance Part III (Cryptoassets, 2022): sector-specific AML/CFT guidance for crypto firms; must be read alongside Parts I and II; covers wallet CDD, blockchain analytics, and stablecoin risks",
          "Travel Rule (UK): effective 1 September 2023 — for cryptoasset transfers of £1,000 or more, the originating VASP must collect and transmit originator and beneficiary information to the receiving VASP (The Money Laundering and Terrorist Financing (Amendment) (No. 2) Regulations 2022)",
          "Unhosted/self-hosted wallets: enhanced due diligence is required when a customer sends to or receives from an unhosted wallet; firms must collect wallet ownership evidence and apply risk-based controls",
          "EDD for high-risk crypto activity: apply EDD for customers using privacy coins (Monero, Zcash), mixing services, darknet-linked addresses, or operating from FATF-listed high-risk countries",
          "Blockchain analytics: the FCA expects firms to use blockchain analytics tools (e.g. Chainalysis, Elliptic) to screen wallet addresses for sanctions exposure and illicit fund provenance",
          "FCA Financial Promotions Regime: since October 2023, crypto promotions must be approved by an FCA-authorised person or by a registered crypto firm — non-compliant promotions carry criminal liability",
          "Stablecoin regulation: HM Treasury is developing a regime for fiat-backed stablecoins; VASPs dealing in stablecoins should monitor FCA and PSR consultation outcomes",
        ],
      },
      {
        title: "Travel Rule — UK Crypto Travel Rule (effective 1 September 2023)",
        items: [
          "Legal basis: The Money Laundering and Terrorist Financing (Amendment) (No. 2) Regulations 2022 — extends the Wire Transfer Regulation (WTR 2017) to cryptoasset transfers between UK-registered VASPs and overseas VASPs",
          "Threshold: £1,000 or equivalent; for transfers below £1,000, the originating VASP must still collect and retain originator and beneficiary name and wallet address but need not transmit it",
          "Required originator information to transmit (above £1,000): full name, distributed ledger address (wallet address), and at least one of: account number, national ID number, customer ID number, or date and place of birth",
          "Required beneficiary information: full name and wallet address; receiving VASP must verify data completeness and conduct risk-based scrutiny",
          "Receiving VASP obligations: verify completeness of incoming data; detect and manage missing/inconsistent information; hold transactions pending verification where data is materially deficient",
          "Sunrise problem — FCA guidance (PS22/1): UK VASPs may proceed with transfers where a counterparty lacks Travel Rule capability, provided: the VASP has taken reasonable steps to transmit, assessed the additional risk, and documented the counterparty's non-compliance status",
          "Unhosted wallet — enhanced measures (above £1,000): collect wallet ownership evidence before processing — accepted methods include a signed cryptographic message, a micro-transaction test, or a signed self-declaration from the customer",
          "Blockchain analytics integration: FCA expects firms to cross-reference wallet addresses against blockchain analytics tools to detect exposure to mixers, darknet markets, ransomware wallets, or OFAC/HMT-sanctioned addresses",
          "Interaction with UK Sanctions: all Travel Rule data must be screened against the UK Sanctions List (OFSI) and HMT Consolidated List before transmitting or receiving virtual asset transfers",
          "Penalties for non-compliance: FCA may impose financial penalties, public censure, or cancel FCA registration; senior managers may be personally liable under the Senior Managers and Certification Regime (SM&CR)",
        ],
      },
    ],
  },
  {
    id: "mas",
    regulator: "MAS",
    fullName: "Monetary Authority of Singapore",
    jurisdiction: "Singapore",
    flag: "🇸🇬",
    accent: "#059669",
    framework: "MAS AML/CFT Notices · TSOFA · CDSA · MAS Guidelines",
    keyLaws: ["Corruption, Drug Trafficking and Other Serious Crimes (Confiscation of Benefits) Act (CDSA)", "Terrorism (Suppression of Financing) Act (TSOFA)", "MAS AML/CFT Notices by sector (e.g. MAS Notice 626 for banks)", "MAS Guidelines on AML/CFT"],
    sections: [
      {
        title: "AML/CFT Programme Requirements",
        items: [
          "Policies, procedures and controls: documented framework addressing CDD, record keeping, suspicious transaction reporting, and training",
          "Compliance officer: appoint an AML/CFT Compliance Officer with adequate seniority and authority",
          "Board and senior management responsibility: ultimate accountability rests with the board; senior management must actively oversee AML/CFT",
          "Regular review: AML/CFT programme must be reviewed and updated to reflect changes in laws, regulations, and business activities",
          "Independent audit: annual assessment of the effectiveness of AML/CFT policies and procedures",
          "Training: all relevant staff must receive AML/CFT training at least annually",
        ],
      },
      {
        title: "Customer Due Diligence",
        items: [
          "CDD triggers: new customers, non-account holders for occasional transactions above SGD 5,000 (cash) or SGD 15,000 (wire transfers)",
          "Standard CDD: verify full name, unique identification number (NRIC/FIN/passport), date of birth, nationality, and residential address",
          "Entities: verify name, registration number, registered address, principal place of business, directors and beneficial owners",
          "Beneficial ownership: identify and verify individuals owning/controlling ≥ 25% of an entity",
          "Simplified CDD: available for lower-risk customers including SGX-listed entities, Singapore government entities, and certain financial institutions",
          "EDD: required for PEPs, high-risk countries, complex or unusual transactions, private banking clients",
          "Timing: CDD must be completed before establishing a business relationship; no exceptions",
        ],
      },
      {
        title: "Politically Exposed Persons",
        items: [
          "Foreign PEPs: automatically high risk — obtain senior management approval, establish source of wealth and funds, conduct enhanced ongoing monitoring",
          "Domestic PEPs: risk-based approach — assess individually",
          "Family members and close associates of PEPs must be identified and assessed",
          "Existing customers who become PEPs must be reviewed and EDD applied",
        ],
      },
      {
        title: "Targeted Financial Sanctions (TFS)",
        items: [
          "Screen all customers and counterparties against MAS Targeted Financial Sanctions list before establishing relationships and on an ongoing basis",
          "United Nations sanctions lists (UNSC Resolutions) and Singapore's own designations apply",
          "Immediately freeze assets and report to MAS if a match is found",
          "No transactions with designated individuals or entities — civil and criminal penalties apply",
        ],
      },
      {
        title: "Suspicious Transaction Reporting (STR)",
        items: [
          "File STRs with the Suspicious Transaction Reporting Office (STRO) of the Singapore Police Force",
          "Report any transaction or attempted transaction where there are reasonable grounds to suspect ML/TF",
          "No minimum threshold",
          "Tipping-off prohibition under CDSA — do not disclose to the subject",
          "File using the online STRO portal; retain a copy of all STRs filed",
        ],
      },
      {
        title: "Wire Transfer Rules",
        items: [
          "Transfers ≥ SGD 1,500 (or equivalent): obtain and retain originator name, account number, and address/ID/DOB",
          "Cross-border transfers: pass originator information through the payment chain",
          "Beneficiary institutions must identify transactions lacking required originator information and apply risk-based measures",
        ],
      },
      {
        title: "Record Keeping",
        items: [
          "All AML/CFT records must be retained for a minimum of 5 years",
          "Records include: CDD documents, account files, business correspondence, transaction records, STR copies",
          "Records must be retrievable promptly and provided to MAS or law enforcement on request",
        ],
      },
      {
        title: "Virtual Asset Service Provider (VASP) Requirements",
        items: [
          "Licensing under the Payment Services Act (PSA) 2019: firms providing Digital Payment Token (DPT) services — buying/selling DPTs, facilitating DPT exchanges, or transferring DPTs on behalf of clients — must hold a Major Payment Institution (MPI) or Standard Payment Institution (SPI) licence",
          "MAS Notice PSN02 (AML/CFT for DPT Service Providers, effective 2020): full AML/CFT programme requirements apply, including CDD, EDD, ongoing monitoring, STR obligations, and record keeping",
          "Travel Rule (MAS Notice PSN01/PSN02): for DPT transfers of SGD 1,500 or more (or equivalent), the originating VASP must obtain and transmit originator and beneficiary name, account/wallet address, and identification number to the receiving VASP — effective January 2020",
          "Unhosted wallet transactions: for transfers to/from unhosted wallets, VASPs must collect wallet address and apply risk-based due diligence; MAS expects use of blockchain analytics tools for wallet screening",
          "EDD for high-risk DPT activity: apply EDD for customers transacting in privacy-enhancing coins, using mixers/tumblers, or transacting with entities in FATF high-risk or non-cooperative jurisdictions",
          "Targeted financial sanctions: screen all DPT addresses and counterparties against MAS TFS list and UN sanctions lists before processing any transaction",
          "Technology risk: MAS Technology Risk Management (TRM) Guidelines apply to DPT service providers — implement controls for custody, key management, and cyber security commensurate with risk",
          "Stablecoin framework: MAS finalised a stablecoin regulatory framework in August 2023; single-currency stablecoins (SCS) pegged to SGD or G10 currencies are regulated — issuers must maintain full reserve backing and meet MAS prudential requirements",
        ],
      },
      {
        title: "Travel Rule — MAS Notice PSN01/PSN02 & Ongoing Requirements",
        items: [
          "Legal basis: MAS Notice PSN01 (Payment Service Providers) and PSN02 (Digital Payment Token Services) — impose Travel Rule obligations on all MPI and SPI licensees conducting DPT transfers; effective January 2020, one of the earliest implementations globally",
          "Threshold: SGD 1,500 or equivalent in any other currency or DPT; below this threshold, VASPs must still collect and retain originator and beneficiary data but need not transmit",
          "Required originator information to transmit (above SGD 1,500): full name, account identifier (wallet address), and at least one of: national ID, residential address, or date and place of birth",
          "Required beneficiary information: full name and account identifier (wallet address); receiving VASP must verify data completeness",
          "Counterparty due diligence: before transmitting, identify whether the receiving entity is a MAS-licensed DPT service provider, a foreign equivalent regulated VASP, or an unregulated entity — adjust risk assessment accordingly",
          "Receiving institution obligations: verify completeness and internal consistency of incoming originator data; apply risk-based scrutiny; reject or place on hold transactions where required information is materially absent",
          "Sunrise problem — MAS approach: where a receiving VASP does not yet have Travel Rule capability, Singapore VASPs may proceed where the transaction risk is assessed as low; document the gap assessment, retain all data, and transmit when technically feasible",
          "Unhosted wallet requirements: for transfers to/from unhosted wallets, VASPs must collect the wallet address, assess ownership risk, and apply EDD for larger or higher-risk transfers; MAS expects use of blockchain analytics to screen wallet history",
          "Protocol interoperability: MAS is technology-neutral — firms may use TRISA, Notabene, TRP, Sygna Bridge, or any equivalent protocol; however, VASPs must ensure their chosen solution achieves practical interoperability with major global counterparties",
          "Record retention: all Travel Rule records must be retained for 5 years from the date of the DPT transfer and made available to MAS on request within a reasonable time",
        ],
      },
    ],
  },
  {
    id: "austrac",
    regulator: "AUSTRAC",
    fullName: "Australian Transaction Reports and Analysis Centre",
    jurisdiction: "Australia",
    flag: "🇦🇺",
    accent: "#D97706",
    framework: "AML/CTF Act 2006 · AML/CTF Rules · AUSTRAC Guidance Notes",
    keyLaws: ["Anti-Money Laundering and Counter-Terrorism Financing Act 2006 (AML/CTF Act)", "Anti-Money Laundering and Counter-Terrorism Financing Rules Instrument 2007", "Financial Transaction Reports Act 1988"],
    sections: [
      {
        title: "AML/CTF Programme — Part A and Part B",
        items: [
          "Part A — General: ML/TF risk assessment; employee due diligence; AML/CTF compliance officer; ongoing training; independent review at least every 3 years; transaction monitoring; enhanced customer due diligence procedures",
          "Part B — Customer Identification: procedures for identifying and verifying customers before providing designated services",
          "Programme must be approved by the board or equivalent governing body",
          "Must be readily accessible and provided to AUSTRAC on request",
          "Review after material change in ML/TF risk environment or business activities",
        ],
      },
      {
        title: "Customer Identification and Verification (CID)",
        items: [
          "Individuals: verify full name, date of birth, and residential address — at least one reliable document (e.g. driver's licence, passport)",
          "Entities: verify full legal name and registered ABN/ACN; identify directors and beneficial owners",
          "Beneficial ownership: identify individuals who own or control ≥ 25% of an entity",
          "Safe harbour rules: compliance with AUSTRAC's prescribed verification procedures provides a defence if a customer is later found to have provided false information",
          "Ongoing customer due diligence (OCDD): review CDD information throughout the relationship; enhanced monitoring for high-risk customers",
          "EDD triggers: PEPs, customers from high-risk countries, high-value or unusual transactions",
        ],
      },
      {
        title: "Threshold Transaction Reports (TTR)",
        items: [
          "Report any physical currency (cash) transactions of AUD $10,000 or more, or the foreign currency equivalent",
          "File with AUSTRAC within 10 business days of the transaction",
          "Aggregation: transactions conducted on behalf of the same person in a 24-hour period must be aggregated",
          "Structuring is an offence under AML/CTF Act s.142; indicators include multiple just-under-threshold transactions",
        ],
      },
      {
        title: "Suspicious Matter Reports (SMR)",
        items: [
          "Report to AUSTRAC as soon as practicable — within 24 hours if terrorism financing is suspected; within 3 business days for all other suspicious matters",
          "No minimum transaction threshold",
          "Report attempted transactions as well as completed ones",
          "Tipping-off prohibition: do not disclose that an SMR has been filed",
          "Retain SMR records for 7 years",
        ],
      },
      {
        title: "International Funds Transfer Instructions (IFTI)",
        items: [
          "Report all cross-border electronic funds transfers (sent or received) — no minimum threshold",
          "File within 10 business days of the day the instruction was sent or received",
          "Includes SWIFT, correspondent banking transfers, remittances, digital wallet transfers meeting the definition",
          "Provide full originator and beneficiary information in the IFTI report",
        ],
      },
      {
        title: "Record Keeping",
        items: [
          "All AML/CTF records must be retained for 7 years (longer than most jurisdictions)",
          "Records include: AML/CTF programme, CDD records, transaction records, TTRs, SMRs, IFTIs",
          "Records must be in English (or readily translatable) and available to AUSTRAC within a reasonable time",
        ],
      },
      {
        title: "Virtual Asset Service Provider (VASP) Requirements",
        items: [
          "Registration required: Digital Currency Exchange (DCE) providers — businesses that exchange digital currency for fiat or other digital currencies — must register with AUSTRAC before operating; unregistered operation is a criminal offence",
          "AML/CTF Rules Part 1A (Digital Currencies, effective 2018): DCE providers are Reporting Entities and must enrol with AUSTRAC, maintain an AML/CTF Programme, and comply with all applicable reporting and record-keeping obligations",
          "Customer identification: DCE providers must identify and verify customers using the same standard as other reporting entities; ID verification is required before conducting any digital currency exchange service above the applicable threshold",
          "Threshold Transaction Reports (TTR): report physical currency transactions of AUD $10,000 or more that involve conversion to or from digital currency within 10 business days",
          "Suspicious Matter Reports (SMR): report suspicious digital currency transactions as soon as practicable — within 24 hours if terrorism financing is suspected, 3 business days otherwise; no minimum threshold",
          "International Funds Transfer Instructions (IFTI): cross-border digital currency transfers may trigger IFTI reporting obligations — monitor AUSTRAC guidance as the regulatory perimeter for crypto IFTIs continues to evolve",
          "Travel Rule: Australia is implementing the FATF Travel Rule for VASPs; AUSTRAC has released consultation on extending wire transfer rules to digital currency transfers — VASPs should monitor and prepare for threshold-based originator/beneficiary data transmission requirements",
          "Blockchain analytics: AUSTRAC expects VASPs to use transaction monitoring and blockchain analytics to detect suspicious patterns including layering through multiple wallets, use of mixers, and rapid high-volume conversions",
        ],
      },
      {
        title: "Travel Rule — AML/CTF Amendment Act 2024 & AUSTRAC Implementation",
        items: [
          "Legal basis: Anti-Money Laundering and Counter-Terrorism Financing Amendment Act 2024 expanded the AML/CTF Act to digital asset service providers; AUSTRAC is empowered to issue rules specifically addressing Travel Rule obligations for virtual asset transfers",
          "Threshold: AUD $1,000 or equivalent — consistent with the FATF standard of USD/EUR $1,000; below threshold, VASPs must retain originator and beneficiary data but need not transmit",
          "Required originator information to transmit: full name, account/wallet address, and either physical address, national ID number, or date and place of birth",
          "Required beneficiary information: full name and account/wallet address; the receiving entity must retain this data and apply risk-based review",
          "Phase-in approach: AUSTRAC has adopted a staged implementation — VASPs must begin collecting Travel Rule data from registration; full peer-to-peer interoperability compliance is expected as the industry matures and Travel Rule protocols are widely adopted",
          "Sunrise problem guidance: AUSTRAC expects VASPs to take a documented risk-based approach — identify counterparty capability, retain originator/beneficiary information internally where transmission is not yet possible, and transmit as soon as technically feasible",
          "Unhosted wallet treatment: for transfers to/from unhosted wallets, collect the wallet address at minimum; apply EDD for transfers above AUD $10,000 or where other risk indicators are present; document the ownership assessment",
          "Wire transfer rules extension: AUSTRAC's Part 5 AML/CTF Rules record-keeping obligations now explicitly apply to digital currency transfers — retain all transfer records for 7 years",
          "SMR interaction: where a Travel Rule-triggering digital currency transfer is also suspicious, both the Travel Rule data transmission obligations and SMR reporting obligations apply; file the SMR within 24 hours (terrorism financing) or 3 business days (other ML/TF)",
          "Enforcement posture: AUSTRAC's history of landmark enforcement actions (e.g. CBA $1.3B, Westpac $1.3B) signals strong willingness to penalise systemic AML failures — VASPs should treat Travel Rule compliance as a day-one priority",
        ],
      },
    ],
  },
  {
    id: "bcb",
    regulator: "BCB / COAF",
    fullName: "Banco Central do Brasil / Conselho de Controle de Atividades Financeiras",
    jurisdiction: "Brazil",
    flag: "🇧🇷",
    accent: "#16A34A",
    framework: "Res. BCB 277/2022 · Lei 9.613/1998 · COAF Regulations",
    keyLaws: ["Lei 9.613/1998 (AML Law, as amended by Lei 12.683/2012)", "Resolução BCB nº 277/2022", "Resolução BCB nº 44/2020 (PEPs)", "COAF Resoluções and Comunicados"],
    sections: [
      {
        title: "Compliance Programme Requirements (Res. BCB 277/2022)",
        items: [
          "Governance: board and senior management must endorse and actively oversee the AML/CFT compliance programme",
          "Compliance officer (Diretor de Prevenção à Lavagem de Dinheiro): designated officer responsible for AML/CFT; must be registered with BCB",
          "Policies and procedures: documented and approved policies addressing all AML/CFT obligations",
          "Internal controls: robust controls proportionate to the nature, scale, and complexity of the institution",
          "Risk-based approach: risk assessment must consider customer profile, products/services, geographic exposure, and distribution channels",
          "Training: all employees must receive AML/CFT training at admission and annually thereafter",
          "Independent audit: annual assessment of the effectiveness of the AML/CFT programme",
        ],
      },
      {
        title: "Know Your Customer (KYC) — Identification and Due Diligence",
        items: [
          "Natural persons: collect full name, CPF (tax ID), date of birth, address, occupation, and income/financial capacity",
          "Legal entities: collect full legal name, CNPJ (tax ID), registered address, nature of business, partners and beneficial owners",
          "Beneficial ownership: identify individuals owning or controlling ≥ 25% of a legal entity (or a lower threshold where risk warrants)",
          "Simplified procedures available for lower-risk customers; enhanced procedures mandatory for high-risk customers",
          "Continuous update: customer information must be updated at minimum every 5 years for standard-risk clients; more frequently for high-risk",
        ],
      },
      {
        title: "Politically Exposed Persons (PEPs) — Res. BCB 44/2020",
        items: [
          "PEP definition: individuals who hold or have held, in the last 5 years, senior public office in Brazil or abroad (executive, legislative, judicial, and military positions)",
          "Family members and close associates of PEPs are also covered",
          "EDD mandatory: obtain senior management approval; establish source of wealth and funds; apply enhanced ongoing monitoring",
          "Duration: PEP enhanced monitoring continues for 5 years after leaving the position",
        ],
      },
      {
        title: "Suspicious Activity Reporting to COAF",
        items: [
          "Obligated parties must file Suspicious Activity Reports (Comunicações de Operações Suspeitas — COS) with COAF",
          "Report any transaction — regardless of value — where there are grounds to suspect ML/TF",
          "Time limit: file within 24 hours for urgent cases; within 1 business day for transactions identified in real time; within the timeframes set in COAF resoluções for other cases",
          "Tipping-off prohibition: do not inform the subject that a COS has been filed",
          "COAF Siscoaf portal: all reports must be filed electronically via the SISCOAF system",
          "Mandatory threshold reports (RIF): some sectors must also file transaction reports meeting defined thresholds (e.g. cash transactions above BRL 50,000 for financial institutions)",
        ],
      },
      {
        title: "Sanctions and Targeted Financial Sanctions",
        items: [
          "Screen customers against the Brazilian AML Council (CSNU) and UN Security Council sanctions lists",
          "OFAC, EU, and other international lists should also be considered in a risk-based approach for institutions with international exposure",
          "Immediately freeze assets of designated persons/entities and report to BCB and relevant authorities",
        ],
      },
      {
        title: "Record Keeping (Lei 9.613/1998 Art. 10)",
        items: [
          "Customer identification records: retain for a minimum of 5 years from the end of the business relationship",
          "Transaction records: retain for a minimum of 5 years from the date of the transaction",
          "COS (suspicious activity reports): retain for 5 years from the date of filing",
          "Records must be available to BCB, COAF, and law enforcement on request",
        ],
      },
      {
        title: "Virtual Asset Service Provider (VASP) Requirements",
        items: [
          "Lei 14.478/2022 (Virtual Assets Law): signed December 2022, establishes the legal framework for virtual asset service providers in Brazil; VASPs must obtain authorisation from the BCB before operating",
          "BCB authorisation: the Banco Central do Brasil is the prudential and AML/CFT regulator for most VASPs in Brazil; firms must apply for and maintain authorisation; operating without authorisation is an offence",
          "AML/CFT obligations under Res. BCB 277/2022: full AML/CFT programme requirements apply to VASPs — risk assessment, KYC/CDD, PEP screening, sanctions screening, transaction monitoring, COAF reporting, and record keeping",
          "COAF reporting: VASPs must file Suspicious Activity Reports (Comunicações de Operações Suspeitas) with COAF for any transaction suggesting ML/TF — no minimum threshold; file via the SISCOAF portal",
          "Travel Rule: Brazil is implementing FATF Recommendation 16 for virtual assets; VASPs must collect and transmit originator and beneficiary information for virtual asset transfers — monitor BCB normative instructions for threshold and implementation details",
          "Sanctions screening: screen all customers, wallet addresses, and counterparties against the Brazilian CSNU list, OFAC SDN list, and UN Security Council sanctions lists before processing transactions",
          "Customer identification for VASPs: collect CPF (individuals) or CNPJ (entities), full name, address, and purpose of the relationship; verify identity before the first transaction; apply EDD for high-value or high-risk crypto activity",
          "CVM jurisdiction: the Brazilian Securities and Exchange Commission (CVM) regulates virtual asset securities (crypto tokens that qualify as securities); VASPs dealing in such tokens must also comply with CVM regulations",
        ],
      },
      {
        title: "Travel Rule — Regra de Viagem (BCB / Lei 14.478/2022)",
        items: [
          "Legal basis: Lei 14.478/2022 (Virtual Assets Law) and BCB Resolution 277/2022 establish the VASP regulatory framework in Brazil; FATF Recommendation 16 applies through BCB normative instructions — monitor Resoluções BCB for specific Travel Rule implementation details and timelines",
          "Threshold: BRL 1,000 or equivalent (aligned with FATF standard); monitor BCB normative instructions for official confirmation and any domestic adjustments to this threshold",
          "Required originator information to transmit: full name, CPF (individuals) or CNPJ (entities), and wallet address or account identifier; the transmitting VASP must ensure this data reaches the receiving institution",
          "Required beneficiary information: full name and wallet address or account identifier; the receiving VASP must retain and verify this information",
          "PIX interaction: transfers involving PIX and digital assets — such as fiat-to-crypto ramps using PIX — may trigger both BCB payment regulation (Resolução BCB 1/2020) and Travel Rule obligations; VASPs must assess both sets of requirements and document compliance for each applicable leg of the transaction",
          "Counterparty identification: before transmitting Travel Rule data, VASPs must identify whether the counterparty is a BCB-authorised VASP or an equivalent regulated entity abroad; apply heightened scrutiny to transfers involving unregulated or unidentified counterparties",
          "Sunrise problem: where a foreign counterparty VASP is not yet Travel Rule compliant, Brazilian VASPs should apply a documented risk-based approach — assess the counterparty's jurisdiction and regulatory status, retain originator/beneficiary information for the 5-year record-keeping period, and transmit as soon as technically feasible",
          "COAF reporting interaction: where a Travel Rule-triggering transaction is also suspicious, both Travel Rule data transmission obligations and COAF SAR reporting (via SISCOAF) apply simultaneously — file the SAR as soon as practicable without informing the subject",
          "Record retention: all Travel Rule records must be retained for 5 years under the BCB AML/CFT framework; records must be retrievable for regulatory inspection",
          "Implementation timeline: BCB is expected to issue specific normative instructions for VASP Travel Rule compliance after full implementation of the VASP authorisation regime; VASPs should begin building Travel Rule infrastructure during the pre-authorisation period to demonstrate readiness",
        ],
      },
    ],
  },
  {
    id: "hkma",
    regulator: "HKMA",
    fullName: "Hong Kong Monetary Authority",
    jurisdiction: "Hong Kong",
    flag: "🇭🇰",
    accent: "#DB2777",
    framework: "AMLO · DTROP · UNATMO · HKMA Guideline",
    keyLaws: ["Anti-Money Laundering and Counter-Terrorist Financing Ordinance (AMLO) Cap. 615", "Drug Trafficking (Recovery of Proceeds) Ordinance (DTROP)", "United Nations (Anti-Terrorism Measures) Ordinance (UNATMO)", "HKMA Guideline on AML/CFT (SPM/ML-CRF)"],
    sections: [
      {
        title: "AML/CFT Programme Requirements (AMLO Sch. 2)",
        items: [
          "Policies and procedures: documented AML/CFT framework covering CDD, record keeping, STR, employee screening, and training",
          "MLRO: appoint a Money Laundering Reporting Officer with sufficient seniority and authority; responsible for receiving internal SARs and filing external reports",
          "Senior management oversight: board and senior management must be actively engaged in AML/CFT governance",
          "Staff screening: implement employee screening procedures proportionate to ML/TF risk",
          "Training: all relevant staff must receive AML/CFT training on joining and regularly thereafter",
          "Independent audit: periodic review of the effectiveness of AML/CFT measures",
        ],
      },
      {
        title: "Customer Due Diligence (AMLO Sch. 2, Part 1–2)",
        items: [
          "Standard CDD triggers: establishing a business relationship; one-off transactions of HKD 120,000 or more (or equivalent); wire transfers of HKD 8,000 or more",
          "Individuals: full name, identity document type and number, date of birth, residential address",
          "Entities: full legal name, registered number, registered address, directors, beneficial owners",
          "Beneficial ownership: identify individuals with ≥ 25% ownership or control",
          "Simplified CDD: available for lower-risk customers such as listed companies and regulated FIs — documented rationale required",
          "EDD: mandatory for PEPs, high-risk countries, correspondent banking, private banking, and complex/unusual transactions",
          "Ongoing monitoring: review transactions for consistency with knowledge of the customer; update CDD on material change",
        ],
      },
      {
        title: "Politically Exposed Persons",
        items: [
          "PEPs include individuals holding prominent public functions in Hong Kong, overseas, or in international organisations, and their family members and known close associates",
          "EDD is mandatory: senior management approval, source of wealth and funds, enhanced ongoing monitoring",
          "Former PEPs: risk-based determination as to when PEP status can be downgraded (typically 12 months or more after leaving office, risk assessed)",
        ],
      },
      {
        title: "Suspicious Transaction Reporting",
        items: [
          "Internal STR: all staff must report suspicion to the MLRO immediately",
          "External report: MLRO files Suspicious Transaction Reports (STRs) to the Joint Financial Intelligence Unit (JFIU) of Hong Kong Police Force",
          "Tipping-off: criminal offence under DTROP s.25A and UNATMO s.12 — do not disclose to the subject",
          "No minimum threshold; attempted transactions must also be reported",
          "JFIU online portal: all STRs filed electronically",
        ],
      },
      {
        title: "Wire Transfers",
        items: [
          "Transfers ≥ HKD 8,000: obtain and retain full originator information (name, account, address or ID or DOB)",
          "Cross-border transfers: originator information must accompany the transfer through the entire payment chain",
          "Beneficiary institutions must identify and apply risk-based measures to transfers lacking required information",
        ],
      },
      {
        title: "Record Keeping (AMLO s.20)",
        items: [
          "CDD records: retain for 6 years after the end of the business relationship",
          "Transaction records: retain for 6 years from the date of the transaction",
          "Records must be in English or Chinese and produced to the HKMA, JFIU, or law enforcement on request",
        ],
      },
      {
        title: "Virtual Asset Service Provider (VASP) Requirements",
        items: [
          "VASP licensing regime (AMLO, as amended by AMLOTO 2022): mandatory licensing for centralised virtual asset exchanges effective 1 June 2023; all VASPs conducting business in Hong Kong or actively marketing to Hong Kong investors must hold a licence from the SFC",
          "Dual-regulator structure: the Securities and Futures Commission (SFC) licences and supervises VASPs dealing in virtual assets (including security tokens); the HKMA oversees stablecoin issuers and may regulate VASPs providing payment-related crypto services",
          "SFC VASP licensing requirements: full AML/CFT programme; fit and proper management; cybersecurity controls; client asset segregation; retail investor access rules; at least 98% of client assets held in cold storage",
          "AML/CFT obligations for VASPs (AMLO Sch. 2 + SFC circulars): VASPs are subject to all standard AMLO CDD, ongoing monitoring, STR, and record-keeping requirements; the SFC has issued detailed AML/CFT guidance specific to the crypto sector",
          "Travel Rule: HKMA and SFC require VASPs to comply with the Travel Rule for virtual asset transfers — for transfers of HKD 8,000 or more, originating VASPs must collect and transmit full originator and beneficiary information to the receiving VASP",
          "Unhosted wallet due diligence: VASPs must apply risk-based measures when customers transfer to/from unhosted wallets; collect wallet address and, for larger transfers, evidence of wallet ownership; SFC expects use of blockchain analytics tools",
          "Retail investor access (SFC 2023 policy): licensed VASPs may serve retail investors subject to enhanced investor protection measures including knowledge assessments, risk disclosure, and suitability requirements",
          "Stablecoin consultation: HKMA issued a consultation in 2023 on regulating fiat-referenced stablecoins (FRS); issuers of HKD or multi-currency FRS will require HKMA authorisation — monitor for finalised regulations",
        ],
      },
      {
        title: "Travel Rule — SFC & HKMA Requirements (AMLO Schedule 2)",
        items: [
          "Legal basis: Anti-Money Laundering and Counter-Terrorist Financing Ordinance (AMLO) Schedule 2 as amended by the AMLOTO 2022; SFC VASP licensing conditions impose full Travel Rule compliance as a condition of licence — non-compliance may result in licence suspension or revocation",
          "Threshold: HKD 8,000 or equivalent in any other currency or virtual asset — one of the highest thresholds globally; above this threshold, full originator and beneficiary information must be collected and transmitted",
          "Below-threshold obligations: for transfers below HKD 8,000, VASPs must still collect and retain originator name and wallet address; data need not be transmitted but must be available to the SFC or HKMA on request",
          "Required originator information to transmit (above HKD 8,000): full name, account/wallet address, and at least one of: national ID or travel document number, residential address, or date and place of birth",
          "Required beneficiary information: full name and account/wallet address; the receiving VASP must conduct reasonable scrutiny of data completeness and consistency",
          "Receiving VASP obligations: verify completeness of incoming Travel Rule data; apply risk-based measures where data is missing or inconsistent; may refuse to process transactions where required information is materially absent",
          "Unhosted wallet requirements — SFC circular (October 2023): for transfers to/from unhosted wallets above HKD 8,000, VASPs must conduct enhanced due diligence including wallet ownership verification; accepted methods include a signed cryptographic message, a micro-transaction test, or a customer self-certification; blockchain analytics screening is expected for all material unhosted wallet transfers",
          "Sunrise problem — SFC guidance: where a counterparty VASP operates in a jurisdiction without a Travel Rule framework, Hong Kong VASPs must assess the additional risks, document counterparty due diligence findings, apply EDD to associated transactions, and retain all originator/beneficiary information pending the counterparty's compliance",
          "Protocol requirements: SFC accepts any internationally recognised Travel Rule protocol (TRISA, Notabene, TRP, Sygna Bridge); VASPs must ensure their chosen protocol achieves practical interoperability with major global counterparties, including Mainland China-connected exchanges and major international VASPs",
          "Enforcement: the SFC has indicated that Travel Rule compliance will be a key inspection focus for newly licensed VASPs; the SFC may conduct on-site inspections and request Travel Rule transmission logs, counterparty due diligence records, and unhosted wallet assessment documentation as part of routine supervision",
        ],
      },
    ],
  },
];

// ─── REGULATORY GUIDELINES PANEL ──────────────────────────────────────────────
function RegulatoryGuidelinesPanel() {
  const C = useTheme();
  const { t } = useTranslation();
  const [selected, setSelected] = useState(null);
  const [openSections, setOpenSections] = useState({});

  function toggleSection(key) {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  }

  function selectRegulator(g) {
    setSelected(g);
    setOpenSections({});
  }

  const guide = selected ? REGULATORY_GUIDELINES.find(g => g.id === selected) : null;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "280px 1fr", gap: 16, height: "calc(100vh - 220px)", minHeight: 500 }}>

      {/* Left — jurisdiction list */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8, overflowY: "auto" }}>
        {REGULATORY_GUIDELINES.map(g => (
          <div key={g.id} onClick={() => selectRegulator(g.id)}
            style={{
              padding: "14px 16px", borderRadius: 10, cursor: "pointer", transition: "all 0.14s",
              background: selected === g.id ? `${g.accent}18` : C.surface,
              border: `1px solid ${selected === g.id ? g.accent : C.border}`,
            }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 22 }}>{g.flag}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: selected === g.id ? g.accent : C.text, fontWeight: 700, fontSize: 14, fontFamily: "'Syne', sans-serif" }}>{g.regulator}</div>
                <div style={{ color: C.textMid, fontSize: 11, marginTop: 1 }}>{g.jurisdiction}</div>
              </div>
              {selected === g.id && <span style={{ color: g.accent, fontSize: 14 }}>→</span>}
            </div>
            <div style={{ color: C.textDim, fontSize: 10, marginTop: 8, lineHeight: 1.5 }}>{g.framework.split(" · ").slice(0, 2).join(" · ")}</div>
          </div>
        ))}
      </div>

      {/* Right — detail */}
      {!guide ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: C.textDim, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12 }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>◎</div>
          <div style={{ fontSize: 14 }}>{t('policies.selectRegulator')}</div>
          <div style={{ fontSize: 12, marginTop: 6, color: C.textDim }}>{t('policies.guidelinesReadOnly')}</div>
        </div>
      ) : (
        <div style={{ overflowY: "auto", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12 }}>
          {/* Header */}
          <div style={{ padding: "22px 28px 18px", borderBottom: `1px solid ${C.border}`, background: `${guide.accent}0C` }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
              <span style={{ fontSize: 36, lineHeight: 1 }}>{guide.flag}</span>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                  <div style={{ color: guide.accent, fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 800 }}>{guide.regulator}</div>
                  <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4, background: `${guide.accent}20`, color: guide.accent, fontFamily: "'DM Mono', monospace", fontWeight: 600 }}>
                    {guide.jurisdiction}
                  </span>
                </div>
                <div style={{ color: C.textMid, fontSize: 13, marginBottom: 8 }}>{guide.fullName}</div>
                <div style={{ color: C.textDim, fontSize: 11, lineHeight: 1.6 }}>{guide.framework}</div>
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 14 }}>
              {guide.keyLaws.map(law => (
                <span key={law} style={{ fontSize: 10, padding: "4px 9px", borderRadius: 4, background: C.bg, border: `1px solid ${C.border}`, color: C.textMid, fontFamily: "'DM Mono', monospace" }}>
                  {law}
                </span>
              ))}
            </div>
          </div>

          {/* Sections */}
          <div style={{ padding: "18px 28px", display: "flex", flexDirection: "column", gap: 10 }}>
            {guide.sections.map((section, si) => {
              const key = `${guide.id}-${si}`;
              const isOpen = openSections[key] !== false; // default open
              return (
                <div key={key} style={{ border: `1px solid ${C.border}`, borderRadius: 9, overflow: "hidden" }}>
                  <button onClick={() => toggleSection(key)}
                    style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "13px 16px", background: C.bg, border: "none", cursor: "pointer", textAlign: "left" }}>
                    <span style={{ color: guide.accent, fontSize: 14, flexShrink: 0, transition: "transform 0.15s", transform: isOpen ? "rotate(90deg)" : "rotate(0deg)" }}>▶</span>
                    <span style={{ color: C.text, fontSize: 13, fontWeight: 600, fontFamily: "'DM Mono', monospace", flex: 1 }}>{section.title}</span>
                    <span style={{ color: C.textDim, fontSize: 10 }}>{t('policies.requirementsCount', { count: section.items.length })}</span>
                  </button>
                  {isOpen && (
                    <div style={{ background: C.surfaceAlt, borderTop: `1px solid ${C.border}` }}>
                      {section.items.map((item, ii) => (
                        <div key={ii} style={{ display: "flex", gap: 12, padding: "10px 16px", borderBottom: ii < section.items.length - 1 ? `1px solid ${C.border}` : "none" }}>
                          <div style={{ width: 6, height: 6, borderRadius: "50%", background: guide.accent, flexShrink: 0, marginTop: 7 }} />
                          <div style={{ color: C.textMid, fontSize: 13, lineHeight: 1.65 }}>{item}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            <div style={{ marginTop: 8, padding: "12px 16px", background: `${guide.accent}0A`, border: `1px solid ${guide.accent}30`, borderRadius: 8 }}>
              <div style={{ color: guide.accent, fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('policies.importantNote')}</div>
              <div style={{ color: C.textDim, fontSize: 12, lineHeight: 1.65 }}>
                {t('policies.importantNoteText', { regulator: guide.regulator })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MAIN POLICIES PAGE ────────────────────────────────────────────────────────
const INITIAL_DOCS = [
  { id: "550e8400-e29b-41d4-a716-446655440001", title: "AML/CFT Policy", version: "v3.2", updated: "2024-02-15", status: "approved", category: "Core", content: "<h2>1. Purpose &amp; Scope</h2><p>This policy establishes the framework for preventing money laundering and terrorist financing across all business activities.</p><h2>2. Risk-Based Approach</h2><p>The firm adopts a risk-based approach in accordance with FATF Recommendation 1, assessing and mitigating ML/TF risks proportionately.</p><h2>3. Customer Due Diligence</h2><p>CDD measures are applied to all customers at onboarding and on an ongoing basis per FATF Recommendations 10–12.</p>", updatedBy: "System" },
  { id: "550e8400-e29b-41d4-a716-446655440002", title: "Customer Due Diligence Procedures", version: "v2.1", updated: "2024-01-20", status: "approved", category: "KYC", content: "<h2>1. Scope</h2><p>These procedures apply to all customer onboarding and ongoing due diligence activities.</p><h2>2. Standard CDD</h2><p>For all customers, we collect and verify: full legal name, date of birth, residential address, and government-issued identification.</p>", updatedBy: "System" },
  { id: "550e8400-e29b-41d4-a716-446655440003", title: "Sanctions Screening Policy", version: "v4.0", updated: "2024-03-01", status: "approved", category: "Sanctions", content: "<h2>1. Overview</h2><p>This policy governs the screening of customers, counterparties and transactions against applicable sanctions lists.</p>", updatedBy: "System" },
  { id: "550e8400-e29b-41d4-a716-446655440004", title: "Transaction Monitoring Rules", version: "v2.5", updated: "2024-02-28", status: "draft", category: "TM", content: "<h2>1. Purpose</h2><p>This document defines the rules and thresholds applied within the transaction monitoring system.</p>", updatedBy: "System" },
];

export default function PoliciesPage({ currentUser, team, company }) {
  const C = useTheme();
  const { t } = useTranslation();
  const { log: auditLog } = useAuditLog(company, currentUser);
  const { toasts, showToast, dismissToast } = useToast();
  const [confirmDelete, setConfirmDelete] = useState(null); // { id, title }
  const [docs, setDocs] = useState(() => {
    try {
      const saved = localStorage.getItem("crg_policy_docs");
      if (saved) return JSON.parse(saved);
    } catch {}
    return INITIAL_DOCS;
  });

  // Load from Supabase on mount (authoritative source)
  useEffect(() => {
    if (!company?.id) return;
    db.from("policy_documents").select("docs_data")
      .eq("company_id", company.id).maybeSingle()
      .then(({ data, error }) => {
        if (error) {
          console.error('[PoliciesPage] Load error:', error.message);
          return;
        }
        if (data?.docs_data) {
          // Handle case where docs_data might be a JSON string that needs parsing
          let docsArray = data.docs_data;
          if (typeof docsArray === 'string') {
            try {
              docsArray = JSON.parse(docsArray);
            } catch (e) {
              console.error('[PoliciesPage] Failed to parse docs_data:', e.message);
              return;
            }
          }

          // Ensure docsArray is actually an array
          if (!Array.isArray(docsArray)) {
            console.error('[PoliciesPage] docs_data is not an array:', docsArray);
            return;
          }

          console.log('[PoliciesPage] Loaded docs_data from database:', docsArray.map(d => ({ id: d.id, title: d.title })));

          // Ensure each document has a unique, non-empty ID and proper string values
          const docsWithIds = docsArray.map((doc, idx) => {
            // Ensure all string fields are actually strings
            const cleanedDoc = {
              ...doc,
              id: typeof doc.id === 'string' ? doc.id : String(doc.id || ''),
              title: typeof doc.title === 'string' ? doc.title : String(doc.title || ''),
              content: typeof doc.content === 'string' ? doc.content : String(doc.content || ''),
              category: typeof doc.category === 'string' ? doc.category : String(doc.category || 'Core'),
              status: typeof doc.status === 'string' ? doc.status : String(doc.status || 'draft'),
              version: typeof doc.version === 'string' ? doc.version : String(doc.version || 'v1.0'),
              updated: typeof doc.updated === 'string' ? doc.updated : String(doc.updated || ''),
              updatedBy: typeof doc.updatedBy === 'string' ? doc.updatedBy : String(doc.updatedBy || ''),
            };

            if (!cleanedDoc.id || cleanedDoc.id.trim() === '') {
              console.warn(`[PoliciesPage] Document at index ${idx} missing valid ID, generating one. Doc:`, doc);
              return { ...cleanedDoc, id: `doc-${Date.now()}-${idx}` };
            }
            return cleanedDoc;
          });

          // Check for duplicate IDs and deduplicate
          const seenIds = new Set();
          const deduped = docsWithIds.map((doc, idx) => {
            if (seenIds.has(doc.id)) {
              console.warn(`[PoliciesPage] Duplicate ID detected: ${doc.id}, generating unique ID. Doc:`, doc);
              const newId = `doc-${Date.now()}-${idx}-${Math.random().toString(36).substr(2, 9)}`;
              seenIds.add(newId);
              return { ...doc, id: newId };
            }
            seenIds.add(doc.id);
            return doc;
          });

          console.log('[PoliciesPage] After deduplication:', deduped.map(d => ({ id: d.id, title: d.title })));
          setDocs(deduped);
          try { localStorage.setItem("crg_policy_docs", JSON.stringify(deduped)); } catch {}
        }
      })
      .catch(err => console.error('[PoliciesPage] Load failed:', err.message));
  }, [company?.id]);

  // Persist to Supabase (and keep localStorage as cache)
  useEffect(() => {
    try { localStorage.setItem("crg_policy_docs", JSON.stringify(docs)); } catch {}
    if (!company?.id) return;

    // Ensure docs_data is properly formatted - clean up any problematic fields
    const docsToSave = docs.map(d => {
      // Only keep primitive values, ensure no circular references
      const clean = {};
      if (d.id && typeof d.id === 'string') clean.id = d.id;
      if (d.title && typeof d.title === 'string') clean.title = d.title;
      if (d.content && typeof d.content === 'string') clean.content = d.content;
      if (d.category && typeof d.category === 'string') clean.category = d.category;
      if (d.status && typeof d.status === 'string') clean.status = d.status;
      if (d.version && typeof d.version === 'string') clean.version = d.version;
      if (d.updated && typeof d.updated === 'string') clean.updated = d.updated;
      if (d.updatedBy && typeof d.updatedBy === 'string') clean.updatedBy = d.updatedBy;
      if (typeof d.uploaded === 'boolean') clean.uploaded = d.uploaded;
      if (d.fileName && typeof d.fileName === 'string') clean.fileName = d.fileName;
      // Include versionHistory if it's an array
      if (Array.isArray(d.versionHistory)) {
        clean.versionHistory = d.versionHistory.map(entry => ({
          version: String(entry?.version || ''),
          status: String(entry?.status || ''),
          updated: String(entry?.updated || ''),
          updatedBy: String(entry?.updatedBy || ''),
          savedAt: String(entry?.savedAt || ''),
        }));
      }
      if (d.next_review_date && typeof d.next_review_date === 'string') clean.next_review_date = d.next_review_date;

      // Set defaults for missing fields
      return {
        id: clean.id || "",
        title: clean.title || "",
        content: clean.content || "",
        category: clean.category || "Core",
        status: clean.status || "draft",
        version: clean.version || "v1.0",
        updated: clean.updated || new Date().toISOString().slice(0, 10),
        updatedBy: clean.updatedBy || "System",
        uploaded: clean.uploaded || false,
        fileName: clean.fileName || "",
        versionHistory: clean.versionHistory || [],
        next_review_date: clean.next_review_date || "",
      };
    });

    // Use UPDATE to save docs (PUT instead of POST)
    // Send docs_data as a JSON string for the JSONB column
    fetch('/api/query?resource=policy_documents', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('amlguard_token') || 'local-mode'}`,
      },
      body: JSON.stringify({
        company_id: company.id,
        docs_data: JSON.stringify(docsToSave), // Send as JSON string for JSONB
        filter: { company_id: company.id },
      }),
    })
      .then(res => {
        if (!res.ok) return res.text().then(e => Promise.reject(e));
        return res.json();
      })
      .then(() => {
        console.log('[PoliciesPage] Docs saved successfully');
      })
      .catch(err => {
        console.error('[PoliciesPage] Save error:', err);
      });
  }, [docs, company?.id]);
  const [editingDoc, setEditingDoc] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [activeTab, setActiveTab] = useState("documents"); // "documents" | "guidelines"
  const [filterCat, setFilterCat] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");
  const [search, setSearch] = useState("");

  const filtered = docs.filter(d => {
    if (filterCat !== "All" && d.category !== filterCat) return false;
    if (filterStatus !== "All" && d.status !== filterStatus) return false;
    if (search && !d.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // Debug: log filtered docs to catch duplicate IDs
  if (filtered.length > 0) {
    const ids = filtered.map(d => d.id);
    const duplicates = ids.filter((id, idx) => ids.indexOf(id) !== idx);
    if (duplicates.length > 0) {
      console.warn('[PoliciesPage] Duplicate IDs detected in filtered docs:', duplicates);
      console.warn('[PoliciesPage] Filtered docs:', filtered.map(d => ({ id: d.id, title: d.title })));
    }
  }

  // Helper to ensure entityId is a valid UUID string, not a timestamp
  function isValidUUID(id) {
    return typeof id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
  }

  function saveDoc(updated) {
    console.log('[PoliciesPage] saveDoc called with:', { id: updated.id, title: updated.title, status: updated.status });
    console.log('[PoliciesPage] Current docs before save:', docs.map(d => ({ id: d.id, title: d.title, status: d.status })));

    setDocs(prev => {
      const result = prev.map(d => {
        const matches = d.id === updated.id;
        console.log(`[PoliciesPage] Comparing doc id="${d.id}" with updated.id="${updated.id}": ${matches}`);
        return matches ? updated : d;
      });
      console.log('[PoliciesPage] Updated docs:', result.map(d => ({ id: d.id, title: d.title, status: d.status })));
      return result;
    });

    setEditingDoc(updated);
    try {
      // Only log if we have a valid UUID ID to avoid audit log errors
      if (isValidUUID(updated.id)) {
        auditLog({
          action: "policy_document_saved",
          module: "policies",
          entityId: String(updated.id),
          entityName: String(updated.title || ''),
          newValue: {
            version: String(updated.version || ''),
            status: String(updated.status || ''),
            category: String(updated.category || ''),
          },
        });
      }
    } catch (e) {
      console.error('[PoliciesPage] Audit log failed:', e.message);
    }
  }

  function createDoc(newDoc) {
    console.log('[PoliciesPage] createDoc called with:', { id: newDoc.id, title: newDoc.title, status: newDoc.status });
    console.log('[PoliciesPage] newDoc object:', newDoc);

    setDocs(prev => {
      const updated = [...prev, newDoc];
      console.log('[PoliciesPage] After adding new doc, docs array:', updated.map(d => ({ id: d.id, title: d.title, status: d.status })));
      return updated;
    });

    setEditingDoc(newDoc);
    showToast(`"${newDoc.title}" ${newDoc.uploaded ? 'uploaded' : 'created'} successfully`, "success");
    try {
      // Only log if we have a valid UUID ID to avoid audit log errors
      if (isValidUUID(newDoc.id)) {
        auditLog({
          action: "policy_document_created",
          module: "policies",
          entityId: String(newDoc.id),
          entityName: String(newDoc.title || ''),
          newValue: {
            category: String(newDoc.category || ''),
            status: String(newDoc.status || ''),
          },
        });
      }
    } catch (e) {
      console.error('[PoliciesPage] Audit log failed:', e.message);
    }
  }

  function deleteDoc(id) {
    const doc = docs.find(d => d.id === id);
    setDocs(prev => prev.filter(d => d.id !== id));
    setConfirmDelete(null);
    if (doc) {
      try {
        // Only log if we have a valid UUID ID to avoid audit log errors
        if (isValidUUID(id)) {
          auditLog({
            action: "policy_document_deleted",
            module: "policies",
            entityId: String(id),
            entityName: String(doc.title || ''),
          });
        }
      } catch (e) {
        console.error('[PoliciesPage] Audit log failed:', e.message);
      }
      showToast(`"${doc.title}" deleted`, "success");
    }
  }

  if (editingDoc) {
    return <DocumentEditor doc={editingDoc} onSave={saveDoc} onClose={() => setEditingDoc(null)} currentUser={currentUser} team={team} company={company} />;
  }

  return (
    <div style={{ fontFamily: "'DM Mono', monospace" }}>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      {confirmDelete && (
        <ConfirmDialog
          open
          title="Delete document?"
          body={`"${confirmDelete.title}" will be permanently deleted. This cannot be undone.`}
          confirmLabel="Delete"
          onConfirm={() => deleteDoc(confirmDelete.id)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
      {showNew && <NewDocModal onClose={() => setShowNew(false)} onCreate={createDoc} currentUser={currentUser} />}
      {showUpload && <UploadModal onUpload={createDoc} onClose={() => setShowUpload(false)} currentUser={currentUser} />}

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <h1 style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: 26, fontWeight: 700, margin: 0 }}>{t('policies.title')}</h1>
          <p style={{ color: C.textMid, margin: "4px 0 0", fontSize: 14 }}>{t('policies.docsCount', { count: docs.length })}</p>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <NotifBell currentUser={currentUser} />
          {activeTab === "documents" && <>
            <Btn variant="ghost" onClick={() => setShowUpload(true)}>⬆ {t('policies.upload')}</Btn>
            <Btn onClick={() => setShowNew(true)}>+ {t('policies.newDocument')}</Btn>
          </>}
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20, borderBottom: `1px solid ${C.border}`, paddingBottom: 0 }}>
        {[
          { id: "documents",  labelKey: "policies.tabMyDocuments",         icon: "◎" },
          { id: "guidelines", labelKey: "policies.tabRegulatoryGuidelines", icon: "⬡" },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            padding: "9px 18px", background: "transparent", border: "none", cursor: "pointer",
            color: activeTab === tab.id ? C.accent : C.textMid,
            borderBottom: `2px solid ${activeTab === tab.id ? C.accent : "transparent"}`,
            fontFamily: "'DM Mono', monospace", fontSize: 13, fontWeight: activeTab === tab.id ? 700 : 400,
            marginBottom: -1, transition: "all 0.15s", display: "flex", alignItems: "center", gap: 6,
          }}>
            <span>{tab.icon}</span> {t(tab.labelKey)}
          </button>
        ))}
      </div>

      {activeTab === "guidelines" && <RegulatoryGuidelinesPanel />}

      {activeTab === "documents" && <>
        {/* Filters */}
        <div style={{ display: "flex", gap: 12, marginBottom: 20, alignItems: "center", flexWrap: "wrap" }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('policies.searchDocuments')} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 13, padding: "8px 14px", fontFamily: "'DM Mono', monospace", outline: "none", width: 220 }} />
          <div style={{ display: "flex", gap: 6 }}>
            {["All", ...CATEGORIES].map(c => (
              <button key={c} onClick={() => setFilterCat(c)} style={{ padding: "6px 12px", borderRadius: 6, fontSize: 11, cursor: "pointer", background: filterCat === c ? C.accentGlow : "transparent", border: `1px solid ${filterCat === c ? C.accent : C.border}`, color: filterCat === c ? C.accent : C.textMid, fontFamily: "'DM Mono', monospace" }}>{c}</button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 6, marginLeft: "auto" }}>
            {["All", "draft", "review", "pending_approval", "approved"].map(s => (
              <button key={s} onClick={() => setFilterStatus(s)} style={{ padding: "6px 12px", borderRadius: 6, fontSize: 11, cursor: "pointer", background: filterStatus === s ? C.accentGlow : "transparent", border: `1px solid ${filterStatus === s ? C.accent : C.border}`, color: filterStatus === s ? C.accent : C.textMid, fontFamily: "'DM Mono', monospace", textTransform: "capitalize" }}>{s.replace("_"," ")}</button>
            ))}
          </div>
        </div>

        {/* Document grid */}
        {filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: C.textDim }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>◎</div>
            <div style={{ fontSize: 14 }}>{t('policies.noDocsMatch')}</div>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14 }}>
            {filtered.map(doc => (
              <DocCard key={doc.id} doc={doc} onOpen={() => setEditingDoc(doc)} onDelete={() => setConfirmDelete({ id: doc.id, title: doc.title })} />
            ))}
          </div>
        )}
      </>}
    </div>
  );
}

function DocCard({ doc, onOpen, onDelete }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [hover, setHover] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // Ensure content is a string (handle case where it might be stored as an object)
  const contentStr = typeof doc.content === 'string' ? doc.content : (doc.content ? String(doc.content) : '');
  const preview = contentStr ? contentStr.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().slice(0, 100) : t('policies.noContent');
  const overdue = isReviewOverdue(doc);

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ background: C.surface, border: `1px solid ${overdue ? "rgba(255,68,68,0.4)" : hover ? C.borderLight : C.border}`, borderRadius: 12, padding: 20, transition: "all 0.15s", cursor: "default" }}
    >
      {/* E-028: Annual review overdue warning */}
      {overdue && (
        <div style={{ fontSize: 10, color: "#FF4444", background: "rgba(255,68,68,0.08)", border: "1px solid rgba(255,68,68,0.25)", borderRadius: 5, padding: "3px 8px", marginBottom: 8, fontFamily: "'DM Mono',monospace" }}>
          ⚠ Annual review overdue — last updated {doc.updated}
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
        <Badge status={doc.status} />
        <span style={{ fontSize: 10, color: C.textDim, background: C.bg, padding: "3px 8px", borderRadius: 4 }}>{doc.category}</span>
      </div>
      <div style={{ color: C.text, fontSize: 15, fontWeight: 600, marginBottom: 6, lineHeight: 1.4, fontFamily: "'Syne', sans-serif" }}>{doc.title}</div>
      <div style={{ color: C.textDim, fontSize: 12, lineHeight: 1.6, marginBottom: 14, minHeight: 38 }}>{preview}…</div>
      {/* E-026: Version history panel */}
      {showHistory && doc.versionHistory?.length > 0 && (
        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 8, marginBottom: 10 }}>
          <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 6 }}>VERSION HISTORY</div>
          {[...doc.versionHistory].reverse().slice(0, 5).map((v, i) => (
            <div key={i} style={{ display: "flex", gap: 10, fontSize: 10, color: C.textMid, padding: "3px 0", borderBottom: `1px solid ${C.border}` }}>
              <span style={{ color: C.accent, fontFamily: "'DM Mono',monospace" }}>{v.version}</span>
              <span>{v.updated}</span>
              <span style={{ marginLeft: "auto", textTransform: "capitalize" }}>{v.status?.replace("_", " ")}</span>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ color: C.accent, fontFamily: "'DM Mono', monospace", fontSize: 11 }}>{doc.version}</span>
          <span style={{ color: C.textDim, fontSize: 11 }}>{doc.updated}</span>
          {doc.versionHistory?.length > 0 && (
            <button onClick={() => setShowHistory(h => !h)} style={{ background: "transparent", border: "none", color: C.textDim, cursor: "pointer", fontSize: 10, fontFamily: "'DM Mono',monospace", padding: 0 }}>
              📋 {doc.versionHistory.length} ver{showHistory ? " ▲" : " ▼"}
            </button>
          )}
          {doc.next_review_date && (
            <span style={{ fontSize: 10, color: overdue ? "#FF4444" : C.textDim }}>
              · review due {doc.next_review_date}
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={onDelete} style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 5, color: C.textDim, padding: "4px 8px", cursor: "pointer", fontSize: 11 }} title={t('common.delete')}>✕</button>
          <button onClick={onOpen} style={{ background: C.accentGlow, border: `1px solid ${C.borderLight}`, borderRadius: 5, color: C.accent, padding: "4px 12px", cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono', monospace" }}>{t('common.open')}</button>
        </div>
      </div>
    </div>
  );
}